import {
  activateOrganization,
  acceptInvitation,
  ApiRequestError,
  archiveClient,
  archiveProject,
  archiveWebsite,
  createAttention,
  createClient,
  createOrganization,
  createProject,
  createWebsite,
  getClient,
  getProject,
  getWebsite,
  inviteMember,
  linkWebsiteToProject,
  listAllClients,
  listAllProjects,
  listAllProjectsForClient,
  listAllWebsites,
  listInvitations,
  listInviteableRoles,
  listMembers,
  listOrganizations,
  listProjectSummariesForClient,
  listWebsitesForClient,
  login,
  logout,
  refreshSession,
  registerUser,
  resolveAttention,
  searchClients,
  updateClient,
  updateProject,
  updateWebsite,
  verifyEmail,
} from "../api/client.js";
import type {
  ClientDetail,
  ClientSummary,
  InvitationSummary,
  MembershipSummary,
  OrganizationSummary,
  ProjectDetail,
  ProjectSummary,
  RoleSummary,
  WebsiteDetail,
  WebsiteSummary,
} from "../api/types.js";
import type { WebsiteEnvironment } from "../workspace/field-map.js";
import {
  clearReadableSessionArtifacts,
  clearSession,
  getAccessToken,
  getOrganizationId,
  readCsrfToken,
  setOrganizationId,
} from "../session/session.js";
import { attemptSessionRestore } from "../session/session-restore.js";
import {
  captureEmailVerificationTokenFromLocation,
  captureInvitationTokenFromLocation,
  clearPendingInvitationToken,
  getPendingInvitationToken,
} from "../session/pending-invitation.js";
import { loginCredentialPolicy } from "./login-credential-policy.js";
import {
  countUpdatedWithinDays,
  latestActivityAt,
  latestByUpdated,
  recentActivity,
  upcomingFromProjects,
  type ActivityItem,
} from "../workspace/activity.js";
import {
  activeProjectUnavailable,
  resolveActiveProject,
  type ActiveProjectResolution,
} from "../workspace/active-project.js";
import {
  formatChangeWhen,
  formatDate,
  formatDateTime,
  formatRelativeActivity,
  greetingForHour,
  relativeDayLabel,
} from "../workspace/format.js";
import { initialsFromName } from "../workspace/initials.js";
import {
  formatClientStatus,
  formatProjectStatus,
  formatWebsiteEnvironment,
  formatWebsiteStatus,
  isPositiveClientStatus,
  isPositiveProjectStatus,
  isPositiveWebsiteStatus,
} from "../workspace/labels.js";
import {
  composeClientHistory,
  composeClientHoldings,
  composeClientSituation,
  composeNextAttention,
  composeProjectWorkspace,
  composeWebsiteWorkspace,
  type ClientHistoryInput,
  type ClientHoldingsInput,
  type ClientStoryInput,
  type NextAttention,
  type ProjectWorkspaceView,
  type SituationRow,
  type WebsiteWorkspaceView,
} from "../workspace/narrative.js";
import { formatAttentionSource } from "../workspace/attention.js";
import { suggestClientSlug } from "../workspace/client-slug.js";
import {
  groupHits,
  searchCatalog,
  type CatalogHit,
  type WorkspaceCatalog,
} from "../workspace/local-search.js";
import {
  resolvePrimaryWebsite,
  type PrimaryWebsiteResolution,
} from "../workspace/primary-website.js";
import { groupTimelineByDay, type TimelineItem } from "../workspace/timeline.js";
import {
  iconActivity,
  iconCalendar,
  iconClients,
  iconFolder,
  iconGlobe,
  iconHome,
  iconInbox,
  iconNote,
  iconPlus,
  iconSearch,
  iconSettings,
  iconSpark,
  iconUser,
} from "./icons.js";

interface SearchScreen {
  readonly name: "search";
  readonly query: string;
  /** null = show catalog; [] = searched with no hits. */
  readonly results: readonly ClientSummary[] | null;
}

type ClientTab = "resumo" | "websites";

interface ClientScreen {
  readonly name: "client";
  readonly client: ClientDetail;
  readonly tab: ClientTab;
  readonly websites: readonly WebsiteSummary[];
  readonly websiteReadDenied: boolean;
  readonly primary: PrimaryWebsiteResolution;
  readonly projects: readonly ProjectSummary[];
  readonly members: ReadonlyMap<string, string>;
  readonly projectReadDenied: boolean;
  readonly active: ActiveProjectResolution | null;
  readonly projectDetail: ProjectDetail | null;
  readonly projectChooserPick: string | null;
}

interface LoadedCatalog {
  readonly clients: readonly ClientSummary[];
  readonly websites: readonly WebsiteSummary[];
  readonly projects: readonly ProjectSummary[];
  readonly clientReadDenied: boolean;
  readonly websiteReadDenied: boolean;
  readonly projectReadDenied: boolean;
}

type Screen =
  | { readonly name: "boot" }
  | { readonly name: "login" }
  | { readonly name: "orgs"; readonly organizations: readonly OrganizationSummary[] }
  | { readonly name: "setup-org" }
  | { readonly name: "dashboard" }
  | { readonly name: "create-client" }
  | {
      readonly name: "edit-client";
      readonly client: ClientDetail;
    }
  | {
      readonly name: "create-website";
      readonly clients: readonly ClientSummary[];
      readonly members: readonly MembershipSummary[];
      readonly preselectedClientId: string | null;
    }
  | {
      readonly name: "edit-website";
      readonly client: ClientDetail;
      readonly website: WebsiteDetail;
    }
  | {
      readonly name: "create-project";
      readonly clients: readonly ClientSummary[];
      readonly websites: readonly WebsiteSummary[];
      readonly members: readonly MembershipSummary[];
      readonly preselectedClientId: string | null;
      readonly preselectedWebsiteId: string | null;
    }
  | {
      readonly name: "edit-project";
      readonly client: ClientDetail;
      readonly project: ProjectDetail;
      readonly linkedWebsite: WebsiteSummary | null;
    }
  | SearchScreen
  | ClientScreen
  | {
      readonly name: "client-loading";
      readonly clientId: string;
      readonly hintName: string | null;
    }
  | {
      readonly name: "websites";
      readonly items: readonly WebsiteSummary[];
      readonly denied: boolean;
    }
  | {
      readonly name: "projects";
      readonly items: readonly ProjectSummary[];
      readonly denied: boolean;
    }
  | {
      readonly name: "website";
      readonly client: ClientDetail;
      readonly website: WebsiteDetail;
      readonly active: ActiveProjectResolution;
      readonly projectDetail: ProjectDetail | null;
      readonly members: ReadonlyMap<string, string>;
      readonly chooserPick: string | null;
    }
  | {
      readonly name: "project";
      readonly client: ClientDetail;
      readonly project: ProjectDetail;
      readonly members: ReadonlyMap<string, string>;
      readonly linkedWebsite: WebsiteSummary | null;
    }
  | {
      readonly name: "definicoes";
      readonly members: readonly MembershipSummary[];
      readonly invitations: readonly InvitationSummary[];
      readonly roles: readonly RoleSummary[];
      readonly denied: boolean;
    }
  | { readonly name: "accept-invitation" }
  | { readonly name: "verify-email-done" };

type NavId = "dashboard" | "clientes" | "websites" | "projetos" | "pesquisar" | "definicoes";

const initialSearchScreen = (): SearchScreen => ({
  name: "search",
  query: "",
  results: null,
});

interface PendingCredentials {
  readonly email: string;
  readonly password: string;
}

interface WebsiteReuse {
  readonly website?: WebsiteDetail;
  readonly active?: ActiveProjectResolution | null;
  readonly projectDetail?: ProjectDetail | null;
}

interface FactEntry {
  readonly label: string;
  readonly value: string | HTMLElement;
}

const root = (): HTMLElement => {
  const el = document.querySelector("#app");
  if (!(el instanceof HTMLElement)) {
    throw new Error("#app missing");
  }
  return el;
};

let screen: Screen = { name: "boot" };
/** When true with busy, shell nav/header stay usable (panel-local load). */
let freezeShell = true;
let busy = false;
let errorMessage: string | null = null;
let pendingCredentials: PendingCredentials | null = null;
let lastSearchScreen: SearchScreen = initialSearchScreen();
let clientScreenStash: ClientScreen | null = null;
let catalog: LoadedCatalog | null = null;
let organizationName: string | null = null;
let paletteOpen = false;
let paletteQuery = "";
let paletteIndex = 0;
let paletteCaret = 0;
let plusOpen = false;
let keysBound = false;
let lastPaintedScreen: Screen["name"] | null = null;

const setError = (error: unknown): void => {
  if (error instanceof ApiRequestError) {
    errorMessage = `${error.code}: ${error.message}`;
    return;
  }
  if (error instanceof Error) {
    errorMessage = error.message;
    return;
  }
  errorMessage = "Erro inesperado";
};

const clearError = (): void => {
  errorMessage = null;
};

const clearPendingCredentials = (): void => {
  pendingCredentials = null;
};

const memberMap = (members: readonly MembershipSummary[]): ReadonlyMap<string, string> => {
  const map = new Map<string, string>();
  for (const member of members) {
    map.set(member.id, member.displayName);
  }
  return map;
};

const responsibleName = (
  ownerMembershipId: string,
  members: ReadonlyMap<string, string>,
): string | null => members.get(ownerMembershipId) ?? null;

const responsibleLabel = (
  ownerMembershipId: string,
  members: ReadonlyMap<string, string>,
): string => responsibleName(ownerMembershipId, members) ?? ownerMembershipId;

const formString = (data: FormData, key: string): string => {
  const value = data.get(key);
  return typeof value === "string" ? value : "";
};

const websiteDetailFromSummary = (website: WebsiteSummary): WebsiteDetail => ({
  ...website,
  notes: null,
});

/** Prefer production/active when several websites share the same projectId. */
const findWebsiteLinkedToProject = (
  projectId: string,
  websites: readonly WebsiteSummary[],
): WebsiteSummary | null => {
  const linked = websites.filter((website) => website.projectId === projectId);
  if (linked.length === 0) {
    return null;
  }
  return (
    linked.find((website) => website.status === "ACTIVE" && website.environment === "PRODUCTION") ??
    linked.find((website) => website.status === "ACTIVE") ??
    linked[0] ??
    null
  );
};

const resolveLinkedWebsiteForProject = async (
  clientId: string,
  projectId: string,
  known: WebsiteSummary | WebsiteDetail | null | undefined,
): Promise<WebsiteSummary | null> => {
  if (known !== undefined) {
    return known;
  }
  const loaded = await loadWebsitesSafe(clientId);
  if (loaded.websiteReadDenied) {
    return null;
  }
  return findWebsiteLinkedToProject(projectId, loaded.websites);
};

const enterProjectScreen = async (args: {
  readonly client: ClientDetail;
  readonly project: ProjectDetail;
  readonly members: ReadonlyMap<string, string>;
  /** Pass known link; `undefined` loads client websites; `null` means none. */
  readonly linkedWebsite?: WebsiteSummary | WebsiteDetail | null;
}): Promise<void> => {
  const linkedWebsite = await resolveLinkedWebsiteForProject(
    args.client.id,
    args.project.id,
    args.linkedWebsite,
  );
  screen = {
    name: "project",
    client: args.client,
    project: args.project,
    members: args.members,
    linkedWebsite,
  };
};

const catalogAsSearch = (loaded: LoadedCatalog): WorkspaceCatalog => ({
  clients: loaded.clients,
  websites: loaded.websites,
  projects: loaded.projects,
});

const stashClientScreen = (state: ClientScreen): void => {
  clientScreenStash = state;
};

const restoreClientScreen = (clientId: string): boolean => {
  if (clientScreenStash?.client.id !== clientId) {
    return false;
  }
  screen = { ...clientScreenStash, tab: "resumo" };
  return true;
};

const completeActiveOrganization = async (
  organizationId: string,
  name: string | null,
): Promise<void> => {
  organizationName = name;
  try {
    await activateOrganization(organizationId);
    clearPendingCredentials();
    await loadAndShowDashboard();
    return;
  } catch (error) {
    if (!(error instanceof ApiRequestError) || error.code !== "INVALID_CSRF_TOKEN") {
      throw error;
    }
  }
  const credentials = pendingCredentials;
  if (credentials === null) {
    throw new Error("Credenciais em memória em falta — volta a entrar.");
  }
  await login(credentials.email, credentials.password, organizationId);
  clearPendingCredentials();
  await loadAndShowDashboard();
};

const afterLoginListOrganizations = async (): Promise<void> => {
  if (await tryAcceptPendingInvitation()) {
    return;
  }
  const organizations = await listOrganizations();
  if (organizations.length === 0) {
    screen = { name: "setup-org" };
    return;
  }
  if (organizations.length === 1) {
    const only = organizations[0];
    if (only === undefined) {
      throw new Error("Organization missing");
    }
    await completeActiveOrganization(only.id, only.name);
    return;
  }
  screen = { name: "orgs", organizations };
};

const resolveOrganizationName = async (organizationId: string): Promise<void> => {
  const organizations = await listOrganizations();
  const match = organizations.find((org) => org.id === organizationId);
  organizationName = match?.name ?? null;
};

const bootstrapAuthenticatedSession = async (): Promise<void> => {
  const outcome = await attemptSessionRestore({
    readCsrfToken,
    getAccessToken,
    getOrganizationId,
    refreshSession,
    clearReadableSessionArtifacts,
  });
  if (outcome.kind === "anonymous") {
    if (getPendingInvitationToken() !== null) {
      screen = { name: "accept-invitation" };
      return;
    }
    screen = { name: "login" };
    return;
  }
  if (outcome.organizationId !== null) {
    await resolveOrganizationName(outcome.organizationId);
    if (await tryAcceptPendingInvitation()) {
      return;
    }
    await loadAndShowDashboard();
    return;
  }
  if (await tryAcceptPendingInvitation()) {
    return;
  }
  await afterLoginListOrganizations();
};

const tryAcceptPendingInvitation = async (): Promise<boolean> => {
  const token = getPendingInvitationToken();
  if (token === null || getAccessToken() === null) {
    return false;
  }
  const accepted = await acceptInvitation(token);
  clearPendingInvitationToken();
  await activateOrganization(accepted.organizationId);
  await resolveOrganizationName(accepted.organizationId);
  await loadAndShowDashboard();
  return true;
};

export const mountApp = (): void => {
  document.documentElement.dataset.theme = "light";
  bindKeys();
  captureInvitationTokenFromLocation();
  const verificationToken = captureEmailVerificationTokenFromLocation();
  screen = { name: "boot" };
  void withBusy(async () => {
    if (verificationToken !== null) {
      await verifyEmail(verificationToken);
      if (getPendingInvitationToken() !== null && getAccessToken() === null) {
        screen = { name: "accept-invitation" };
        return;
      }
      if (getPendingInvitationToken() === null) {
        screen = { name: "verify-email-done" };
        return;
      }
    }
    await bootstrapAuthenticatedSession();
  });
};

const withBusy = async (
  action: () => Promise<void>,
  options?: { readonly freezeShell?: boolean; readonly skipInitialRender?: boolean },
): Promise<void> => {
  busy = true;
  freezeShell = options?.freezeShell ?? true;
  clearError();
  if (!(options?.skipInitialRender ?? false)) {
    render();
  }
  try {
    await action();
  } catch (error) {
    setError(error);
    if (screen.name === "boot") {
      clearPendingCredentials();
      clearReadableSessionArtifacts();
      screen = { name: "login" };
    } else if (screen.name === "login") {
      clearPendingCredentials();
      clearSession();
    }
  } finally {
    busy = false;
    freezeShell = true;
    render();
  }
};

const shellIsFrozen = (): boolean => busy && freezeShell;

const contextClientName = (): string | null => {
  switch (screen.name) {
    case "client":
    case "website":
    case "project":
    case "edit-client":
    case "edit-website":
    case "edit-project":
      return screen.client.name;
    case "client-loading":
      return screen.hintName;
    default:
      return null;
  }
};

const inWorkspace = (): boolean =>
  getAccessToken() !== null &&
  getOrganizationId() !== null &&
  screen.name !== "login" &&
  screen.name !== "orgs" &&
  screen.name !== "setup-org" &&
  screen.name !== "accept-invitation" &&
  screen.name !== "verify-email-done";

const syncDocumentTitle = (): void => {
  const clientName = contextClientName();
  document.title = clientName === null ? "UTURA OS" : `UTURA OS · ${clientName}`;
};

const bindKeys = (): void => {
  if (keysBound) {
    return;
  }
  keysBound = true;
  document.addEventListener("keydown", onGlobalKeydown);
};

const onGlobalKeydown = (event: KeyboardEvent): void => {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
    event.preventDefault();
    if (!inWorkspace()) {
      return;
    }
    openPalette();
    return;
  }
  if (event.key === "Escape") {
    if (paletteOpen || plusOpen) {
      paletteOpen = false;
      plusOpen = false;
      render();
    }
    return;
  }
  if (!paletteOpen) {
    return;
  }
  const hits = currentPaletteHits();
  if (event.key === "ArrowDown") {
    event.preventDefault();
    paletteIndex = hits.length === 0 ? 0 : (paletteIndex + 1) % hits.length;
    render();
    return;
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    paletteIndex = hits.length === 0 ? 0 : (paletteIndex - 1 + hits.length) % hits.length;
    render();
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    const hit = hits[paletteIndex];
    if (hit !== undefined) {
      void selectPaletteHit(hit);
    }
  }
};

const currentPaletteHits = (): readonly CatalogHit[] => {
  if (catalog === null) {
    return [];
  }
  return searchCatalog(paletteQuery, catalogAsSearch(catalog));
};

const openPalette = (): void => {
  plusOpen = false;
  paletteOpen = true;
  paletteQuery = "";
  paletteIndex = 0;
  paletteCaret = 0;
  if (catalog === null) {
    void withBusy(async () => {
      catalog = await loadCatalog();
      paletteOpen = true;
    });
    return;
  }
  render();
};

const closeOverlays = (): void => {
  paletteOpen = false;
  plusOpen = false;
};

const render = (): void => {
  const app = root();
  app.replaceChildren();
  syncDocumentTitle();
  app.append(renderHeader());
  if (inWorkspace()) {
    const layout = document.createElement("div");
    layout.className = "shell";
    layout.append(renderNav(), renderMain());
    app.append(layout);
  } else {
    app.append(renderMain());
  }
  if (paletteOpen) {
    app.append(renderPalette());
  }
};

const renderHeader = (): HTMLElement => {
  const header = document.createElement("header");
  header.className = "app-bar";
  const brand = document.createElement("div");
  brand.className = "brand";
  const title = document.createElement("h1");
  title.textContent = "UTURA OS";
  brand.append(title);
  const clientName = contextClientName();
  const echoHeroName = screen.name === "client" || screen.name === "client-loading";
  if (clientName !== null && !echoHeroName) {
    const context = document.createElement("p");
    context.className = "brand-context";
    context.textContent = clientName;
    brand.append(context);
  }
  header.append(brand);
  if (inWorkspace()) {
    const actions = document.createElement("div");
    actions.className = "toolbar";
    const search = document.createElement("button");
    search.type = "button";
    search.className = "secondary search-trigger";
    search.disabled = shellIsFrozen();
    search.setAttribute("aria-keyshortcuts", "Control+K Meta+K");
    search.innerHTML = `<span class="icon">${iconSearch()}</span> Pesquisar <span class="nav-hint">Ctrl+K</span>`;
    search.addEventListener("click", () => {
      openPalette();
    });
    actions.append(search, renderPlusMenu(), renderLogout());
    header.append(actions);
  } else if (getAccessToken() !== null && screen.name !== "login") {
    const actions = document.createElement("div");
    actions.className = "toolbar";
    actions.append(renderLogout());
    header.append(actions);
  }
  return header;
};

const renderLogout = (): HTMLElement => {
  const out = document.createElement("button");
  out.type = "button";
  out.className = "secondary";
  out.textContent = "Sair";
  out.disabled = shellIsFrozen();
  out.addEventListener("click", () => {
    void withBusy(async () => {
      await logout();
      clearPendingCredentials();
      clearReadableSessionArtifacts();
      clientScreenStash = null;
      lastSearchScreen = initialSearchScreen();
      catalog = null;
      organizationName = null;
      closeOverlays();
      screen = { name: "login" };
    });
  });
  return out;
};

const renderPlusMenu = (): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "plus-wrap";
  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("aria-haspopup", "menu");
  button.setAttribute("aria-expanded", plusOpen ? "true" : "false");
  button.setAttribute("aria-label", "Ações rápidas");
  button.disabled = shellIsFrozen();
  button.innerHTML = `<span class="icon">${iconPlus()}</span>`;
  button.addEventListener("click", () => {
    plusOpen = !plusOpen;
    paletteOpen = false;
    render();
  });
  wrap.append(button);
  if (!plusOpen) {
    return wrap;
  }
  const menu = document.createElement("div");
  menu.className = "plus-menu";
  menu.setAttribute("role", "menu");
  const items: readonly {
    readonly label: string;
    readonly icon: string;
    readonly enabled: boolean;
    readonly onSelect: (() => void) | null;
  }[] = [
    {
      label: "Novo Cliente",
      icon: iconClients(),
      enabled: true,
      onSelect: (): void => {
        plusOpen = false;
        clearError();
        screen = { name: "create-client" };
        render();
      },
    },
    {
      label: "Novo Website",
      icon: iconGlobe(),
      enabled: true,
      onSelect: (): void => {
        plusOpen = false;
        clearError();
        void withBusy(async () => {
          await openCreateWebsite(null);
        });
      },
    },
    {
      label: "Novo Projeto",
      icon: iconFolder(),
      enabled: true,
      onSelect: (): void => {
        plusOpen = false;
        clearError();
        void withBusy(async () => {
          const context = projectCreateContextFromScreen();
          await openCreateProject(context.clientId, context.websiteId);
        });
      },
    },
    { label: "Nova Nota", icon: iconNote(), enabled: false, onSelect: null },
  ];
  for (const item of items) {
    const action = document.createElement("button");
    action.type = "button";
    action.disabled = !item.enabled || shellIsFrozen();
    if (item.enabled) {
      action.innerHTML = `<span class="icon">${item.icon}</span><span>${item.label}</span>`;
      action.addEventListener("click", () => {
        item.onSelect?.();
      });
    } else {
      action.title = "Criação ainda não disponível neste workspace — sem fluxo de criação na SPA.";
      action.innerHTML = `<span class="icon">${item.icon}</span><span>${item.label}<span class="hint">Indisponível neste workspace</span></span>`;
    }
    menu.append(action);
  }
  wrap.append(menu);
  return wrap;
};

const activeNav = (): NavId => {
  switch (screen.name) {
    case "dashboard":
      return "dashboard";
    case "search":
    case "create-client":
    case "edit-client":
    case "create-website":
    case "create-project":
    case "client":
    case "client-loading":
      return screen.name === "client" && screen.tab === "websites" ? "websites" : "clientes";
    case "edit-website":
    case "websites":
    case "website":
      return "websites";
    case "edit-project":
    case "projects":
    case "project":
      return "projetos";
    case "definicoes":
      return "definicoes";
    default:
      return "dashboard";
  }
};

const renderNav = (): HTMLElement => {
  const nav = document.createElement("nav");
  nav.className = "shell-nav";
  nav.setAttribute("aria-label", "Navegação principal");
  const items: readonly {
    readonly id: NavId;
    readonly label: string;
    readonly icon: string;
    readonly live: boolean;
    readonly hint: string | null;
  }[] = [
    { id: "dashboard", label: "Dashboard", icon: iconHome(), live: true, hint: null },
    { id: "clientes", label: "Clientes", icon: iconClients(), live: true, hint: null },
    { id: "websites", label: "Websites", icon: iconGlobe(), live: true, hint: null },
    { id: "projetos", label: "Projetos", icon: iconFolder(), live: true, hint: null },
    { id: "pesquisar", label: "Pesquisar", icon: iconSearch(), live: true, hint: "Ctrl+K" },
    {
      id: "definicoes",
      label: "Definições",
      icon: iconSettings(),
      live: true,
      hint: null,
    },
  ];
  const current = activeNav();
  for (const item of items) {
    const button = document.createElement("button");
    button.type = "button";
    const isActive = item.id === current || (item.id === "pesquisar" && paletteOpen);
    button.className = isActive ? "nav-item active" : "nav-item";
    button.disabled = shellIsFrozen() || !item.live;
    if (!item.live) {
      button.title = "Indisponível neste workspace — ainda sem ecrã de definições.";
    }
    button.innerHTML = `<span class="icon">${item.icon}</span>${escapeHtml(item.label)}${
      item.hint === null ? "" : `<span class="nav-hint">${escapeHtml(item.hint)}</span>`
    }`;
    if (item.live) {
      button.addEventListener("click", () => {
        onNav(item.id);
      });
    }
    nav.append(button);
  }
  return nav;
};

const onNav = (id: NavId): void => {
  clearError();
  closeOverlays();
  if (id === "pesquisar") {
    openPalette();
    return;
  }
  if (id === "dashboard") {
    void withBusy(async () => {
      await loadAndShowDashboard();
    });
    return;
  }
  if (id === "clientes") {
    void withBusy(async () => {
      await openClientes();
    });
    return;
  }
  if (id === "websites") {
    void withBusy(async () => {
      await goWebsites();
    });
    return;
  }
  if (id === "projetos") {
    void withBusy(async () => {
      await goProjetos();
    });
    return;
  }
  void withBusy(async () => {
    await openDefinicoes();
  });
};

const renderMain = (): HTMLElement => {
  const panel = document.createElement("section");
  const fade = lastPaintedScreen !== screen.name;
  lastPaintedScreen = screen.name;
  panel.className = fade ? "panel stack fade-in" : "panel stack";
  if (errorMessage !== null) {
    const error = document.createElement("div");
    error.className = "error";
    error.setAttribute("role", "alert");
    error.textContent = errorMessage;
    panel.append(error);
  }
  switch (screen.name) {
    case "boot":
      panel.append(renderBoot());
      break;
    case "login":
      panel.append(renderLogin());
      break;
    case "orgs":
      panel.append(renderOrgs(screen.organizations));
      break;
    case "setup-org":
      panel.append(renderSetupOrg());
      break;
    case "create-client":
      panel.append(renderCreateClient());
      break;
    case "edit-client":
      panel.append(renderEditClient(screen.client));
      break;
    case "create-website":
      panel.append(renderCreateWebsite(screen));
      break;
    case "edit-website":
      panel.append(renderEditWebsite(screen.client, screen.website));
      break;
    case "create-project":
      panel.append(renderCreateProject(screen));
      break;
    case "edit-project":
      panel.append(renderEditProject(screen.client, screen.project));
      break;
    case "dashboard":
      panel.append(renderDashboard());
      break;
    case "search":
      panel.append(renderSearch(screen));
      break;
    case "client":
      panel.append(renderClient(screen));
      break;
    case "client-loading":
      panel.append(renderClientLoading(screen.hintName));
      break;
    case "websites":
      panel.append(renderOrgWebsites(screen.items, screen.denied));
      break;
    case "projects":
      panel.append(renderOrgProjects(screen.items, screen.denied));
      break;
    case "website":
      panel.append(
        renderWebsite(
          screen.client,
          screen.website,
          screen.active,
          screen.projectDetail,
          screen.members,
          screen.chooserPick,
        ),
      );
      break;
    case "project":
      panel.append(
        renderProjectScreen(screen.client, screen.project, screen.members, screen.linkedWebsite),
      );
      break;
    case "definicoes":
      panel.append(renderDefinicoes(screen));
      break;
    case "accept-invitation":
      panel.append(renderAcceptInvitation());
      break;
    case "verify-email-done":
      panel.append(renderVerifyEmailDone());
      break;
  }
  return panel;
};

const renderBoot = (): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.textContent = "A restaurar sessão…";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent = "A verificar a sessão segura.";
  wrap.append(heading, intro);
  return wrap;
};

const renderLogin = (): HTMLElement => {
  if (
    loginCredentialPolicy.prefillsOperatorEmail ||
    loginCredentialPolicy.prefillsOperatorPassword ||
    loginCredentialPolicy.exposesDemoLoginControl
  ) {
    throw new Error("F05: SPA must not prefill credentials or expose Demo login");
  }

  const form = document.createElement("form");
  form.className = "stack login-form";
  const heading = document.createElement("h2");
  heading.textContent = "Entrar";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent = "Workspace do operador — clientes, websites e projetos.";
  form.append(heading, intro);

  const emailLabel = document.createElement("label");
  emailLabel.textContent = "E-mail";
  const emailInput = document.createElement("input");
  emailInput.name = "email";
  emailInput.type = "email";
  emailInput.autocomplete = "username";
  emailInput.required = true;
  emailLabel.append(emailInput);

  const passwordLabel = document.createElement("label");
  passwordLabel.textContent = "Palavra-passe";
  const passwordInput = document.createElement("input");
  passwordInput.name = "password";
  passwordInput.type = "password";
  passwordInput.autocomplete = "current-password";
  passwordInput.required = true;
  passwordInput.minLength = 12;
  passwordLabel.append(passwordInput);

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A entrar…" : "Entrar";
  submit.disabled = busy;
  form.append(emailLabel, passwordLabel, submit);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const email = emailInput.value.trim();
    const password = passwordInput.value;
    void withBusy(async () => {
      pendingCredentials = { email, password };
      await login(email, password);
      await afterLoginListOrganizations();
    });
  });
  return form;
};

const renderOrgs = (organizations: readonly OrganizationSummary[]): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Organização";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent = "Escolhe a organização ativa.";
  wrap.append(heading, intro);
  const list = document.createElement("ul");
  list.className = "list";
  for (const org of organizations) {
    const li = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "item";
    button.disabled = busy;
    button.innerHTML = `<strong>${escapeHtml(org.name)}</strong><span class="muted mono">${escapeHtml(org.slug)}</span>`;
    button.addEventListener("click", () => {
      void withBusy(async () => {
        await completeActiveOrganization(org.id, org.name);
      });
    });
    li.append(button);
    list.append(li);
  }
  wrap.append(list);
  return wrap;
};

const renderSetupOrg = (): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Criar organização";
  const intro = document.createElement("p");
  intro.textContent = "Ainda não tens nenhuma organização.";
  const hint = document.createElement("p");
  hint.className = "muted";
  hint.textContent =
    "Cria uma organização para continuar. Contas admin sem org ficam aqui até criares a primeira.";
  wrap.append(heading, intro, hint);
  const form = document.createElement("form");
  form.className = "stack";
  form.innerHTML = `
    <label>Nome<input name="name" type="text" required minlength="2" maxlength="160" placeholder="A minha agência" /></label>
    <label>Slug<input name="slug" type="text" required minlength="2" maxlength="80" pattern="[a-z0-9]+(?:-[a-z0-9]+)*" placeholder="minha-agencia" /></label>
  `;
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A criar…" : "Criar organização";
  submit.disabled = busy;
  form.append(submit);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const data = new FormData(form);
    const name = formString(data, "name").trim();
    const slug = formString(data, "slug").trim();
    void withBusy(async () => {
      const created = await createOrganization(name, slug);
      await completeActiveOrganization(created.organizationId, name);
    });
  });
  wrap.append(form);
  return wrap;
};

const openDefinicoes = async (): Promise<void> => {
  try {
    const [members, invitations, roles] = await Promise.all([
      listMembers(),
      listInvitations(),
      listInviteableRoles(),
    ]);
    screen = {
      name: "definicoes",
      members,
      invitations: invitations.filter((invitation) => invitation.status === "PENDING"),
      roles,
      denied: false,
    };
  } catch (error) {
    if (
      error instanceof ApiRequestError &&
      (error.status === 403 || error.code === "FORBIDDEN" || error.code === "PERMISSION_DENIED")
    ) {
      screen = {
        name: "definicoes",
        members: [],
        invitations: [],
        roles: [],
        denied: true,
      };
      return;
    }
    throw error;
  }
};

const renderDefinicoes = (state: {
  readonly members: readonly MembershipSummary[];
  readonly invitations: readonly InvitationSummary[];
  readonly roles: readonly RoleSummary[];
  readonly denied: boolean;
}): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Equipa";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent = "Membros da organização e convites pendentes.";
  wrap.append(heading, intro);

  if (state.denied) {
    const denied = document.createElement("p");
    denied.className = "error";
    denied.setAttribute("role", "alert");
    denied.textContent = "Sem permissão para gerir membros ou convites nesta organização.";
    wrap.append(denied);
    return wrap;
  }

  const membersHeading = document.createElement("h3");
  membersHeading.textContent = "Membros";
  wrap.append(membersHeading);
  const membersList = document.createElement("ul");
  membersList.className = "list";
  const activeMembers = state.members.filter(
    (member) => member.status === undefined || member.status === "ACTIVE",
  );
  if (activeMembers.length === 0) {
    const empty = document.createElement("li");
    empty.className = "muted";
    empty.textContent = "Sem membros ativos.";
    membersList.append(empty);
  } else {
    for (const member of activeMembers) {
      const li = document.createElement("li");
      const email =
        member.email === undefined || member.email.length === 0 ? "" : ` · ${member.email}`;
      li.textContent = `${member.displayName}${email}`;
      membersList.append(li);
    }
  }
  wrap.append(membersList);

  const invitesHeading = document.createElement("h3");
  invitesHeading.textContent = "Convites pendentes";
  wrap.append(invitesHeading);
  const invitesList = document.createElement("ul");
  invitesList.className = "list";
  if (state.invitations.length === 0) {
    const empty = document.createElement("li");
    empty.className = "muted";
    empty.textContent = "Nenhum convite pendente.";
    invitesList.append(empty);
  } else {
    for (const invitation of state.invitations) {
      const li = document.createElement("li");
      const role = state.roles.find((item) => item.id === invitation.roleId);
      const roleLabel = role?.name ?? invitation.roleId;
      li.textContent = `${invitation.email} · ${roleLabel}`;
      invitesList.append(li);
    }
  }
  wrap.append(invitesList);

  const inviteHeading = document.createElement("h3");
  inviteHeading.textContent = "Convidar membro";
  wrap.append(inviteHeading);

  const form = document.createElement("form");
  form.className = "stack";
  form.setAttribute("data-testid", "invite-member-form");

  const emailLabel = document.createElement("label");
  emailLabel.textContent = "E-mail";
  const emailInput = document.createElement("input");
  emailInput.name = "email";
  emailInput.type = "email";
  emailInput.required = true;
  emailInput.autocomplete = "email";
  emailLabel.append(emailInput);

  const roleLabel = document.createElement("label");
  roleLabel.textContent = "Função";
  const roleSelect = document.createElement("select");
  roleSelect.name = "roleId";
  roleSelect.required = true;
  for (const role of state.roles) {
    const option = document.createElement("option");
    option.value = role.id;
    option.textContent = role.name;
    if (role.key === "member") {
      option.selected = true;
    }
    roleSelect.append(option);
  }
  roleLabel.append(roleSelect);

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A convidar…" : "Convidar";
  submit.disabled = busy || state.roles.length === 0;

  form.append(emailLabel, roleLabel, submit);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const email = emailInput.value.trim();
    const roleId = roleSelect.value;
    void withBusy(async () => {
      const inviteResult = await inviteMember({
        email,
        roleId,
        organizationName: organizationName ?? "Organization",
        inviterName: "Operador",
      });
      if (inviteResult.delivery === "FAILED") {
        const ref = inviteResult.errorId === undefined ? "" : ` (ref: ${inviteResult.errorId})`;
        throw new Error(
          `Convite criado, mas o e-mail SMTP falhou${ref}. Verifique a configuração de correio.`,
        );
      }
      await openDefinicoes();
    });
  });
  wrap.append(form);
  return wrap;
};

const renderAcceptInvitation = (): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.textContent = "Aceitar convite";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent =
    "Cria uma conta com o e-mail convidado (ou entra), verifica o e-mail se pedido, e aceita o convite.";
  wrap.append(heading, intro);

  const token = getPendingInvitationToken();
  if (token === null) {
    const missing = document.createElement("p");
    missing.className = "error";
    missing.setAttribute("role", "alert");
    missing.textContent = "Ligação de convite inválida ou expirada.";
    wrap.append(missing);
    return wrap;
  }

  const registerForm = document.createElement("form");
  registerForm.className = "stack";
  registerForm.setAttribute("data-testid", "invite-register-form");
  const registerHeading = document.createElement("h3");
  registerHeading.textContent = "Criar conta";

  const displayLabel = document.createElement("label");
  displayLabel.textContent = "Nome";
  const displayInput = document.createElement("input");
  displayInput.name = "displayName";
  displayInput.type = "text";
  displayInput.required = true;
  displayInput.minLength = 2;
  displayInput.maxLength = 120;
  displayInput.autocomplete = "name";
  displayLabel.append(displayInput);

  const registerEmailLabel = document.createElement("label");
  registerEmailLabel.textContent = "E-mail";
  const registerEmailInput = document.createElement("input");
  registerEmailInput.name = "email";
  registerEmailInput.type = "email";
  registerEmailInput.required = true;
  registerEmailInput.autocomplete = "username";
  registerEmailLabel.append(registerEmailInput);

  const registerPasswordLabel = document.createElement("label");
  registerPasswordLabel.textContent = "Palavra-passe";
  const registerPasswordInput = document.createElement("input");
  registerPasswordInput.name = "password";
  registerPasswordInput.type = "password";
  registerPasswordInput.required = true;
  registerPasswordInput.minLength = 12;
  registerPasswordInput.autocomplete = "new-password";
  registerPasswordLabel.append(registerPasswordInput);

  const registerSubmit = document.createElement("button");
  registerSubmit.type = "submit";
  registerSubmit.textContent = busy ? "A criar…" : "Criar conta";
  registerSubmit.disabled = busy;
  registerForm.append(
    registerHeading,
    displayLabel,
    registerEmailLabel,
    registerPasswordLabel,
    registerSubmit,
  );
  registerForm.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const email = registerEmailInput.value.trim();
    const password = registerPasswordInput.value;
    const displayName = displayInput.value.trim();
    void withBusy(async () => {
      await registerUser({ email, password, displayName });
      pendingCredentials = { email, password };
      screen = { name: "verify-email-done" };
    });
  });

  const loginForm = document.createElement("form");
  loginForm.className = "stack";
  loginForm.setAttribute("data-testid", "invite-login-form");
  const loginHeading = document.createElement("h3");
  loginHeading.textContent = "Já tenho conta";

  const loginEmailLabel = document.createElement("label");
  loginEmailLabel.textContent = "E-mail";
  const loginEmailInput = document.createElement("input");
  loginEmailInput.name = "email";
  loginEmailInput.type = "email";
  loginEmailInput.required = true;
  loginEmailInput.autocomplete = "username";
  loginEmailLabel.append(loginEmailInput);

  const loginPasswordLabel = document.createElement("label");
  loginPasswordLabel.textContent = "Palavra-passe";
  const loginPasswordInput = document.createElement("input");
  loginPasswordInput.name = "password";
  loginPasswordInput.type = "password";
  loginPasswordInput.required = true;
  loginPasswordInput.minLength = 12;
  loginPasswordInput.autocomplete = "current-password";
  loginPasswordLabel.append(loginPasswordInput);

  const loginSubmit = document.createElement("button");
  loginSubmit.type = "submit";
  loginSubmit.textContent = busy ? "A entrar…" : "Entrar e aceitar";
  loginSubmit.disabled = busy;
  loginForm.append(loginHeading, loginEmailLabel, loginPasswordLabel, loginSubmit);
  loginForm.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const email = loginEmailInput.value.trim();
    const password = loginPasswordInput.value;
    void withBusy(async () => {
      pendingCredentials = { email, password };
      await login(email, password);
      if (!(await tryAcceptPendingInvitation())) {
        await afterLoginListOrganizations();
      }
    });
  });

  wrap.append(registerForm, loginForm);
  return wrap;
};

const renderVerifyEmailDone = (): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  const pendingInvite = getPendingInvitationToken() !== null;
  const justRegistered = pendingCredentials !== null && getAccessToken() === null;
  heading.textContent = justRegistered ? "Conta criada" : "E-mail verificado";
  const intro = document.createElement("p");
  intro.className = "muted";
  if (justRegistered) {
    intro.textContent = pendingInvite
      ? "Abre o e-mail de verificação (Mailpit em desenvolvimento), confirma o endereço, e depois entra para aceitar o convite."
      : "Abre o e-mail de verificação e confirma o endereço antes de entrar.";
  } else {
    intro.textContent = pendingInvite
      ? "Entra com a conta verificada para aceitar o convite."
      : "Podes entrar na conta.";
  }
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Continuar";
  button.addEventListener("click", () => {
    screen = pendingInvite ? { name: "accept-invitation" } : { name: "login" };
    render();
  });
  wrap.append(heading, intro, button);
  return wrap;
};

const renderCreateClient = (): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Novo cliente";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent =
    "Cria um cliente na organização ativa. Fica disponível na pesquisa e no workspace.";
  wrap.append(heading, intro);

  const form = document.createElement("form");
  form.className = "stack";

  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Nome";
  const nameInput = document.createElement("input");
  nameInput.name = "name";
  nameInput.type = "text";
  nameInput.required = true;
  nameInput.minLength = 2;
  nameInput.maxLength = 160;
  nameInput.placeholder = "Nome do cliente";
  nameInput.autocomplete = "organization";
  nameLabel.append(nameInput);

  const slugLabel = document.createElement("label");
  slugLabel.textContent = "Slug";
  const slugInput = document.createElement("input");
  slugInput.name = "slug";
  slugInput.type = "text";
  slugInput.required = true;
  slugInput.minLength = 2;
  slugInput.maxLength = 80;
  slugInput.pattern = "[a-z0-9]+(?:-[a-z0-9]+)*";
  slugInput.placeholder = "nome-do-cliente";
  slugInput.autocomplete = "off";
  slugLabel.append(slugInput);

  let slugTouched = false;
  nameInput.addEventListener("input", () => {
    if (slugTouched) {
      return;
    }
    slugInput.value = suggestClientSlug(nameInput.value);
  });
  slugInput.addEventListener("input", () => {
    slugTouched = slugInput.value.trim().length > 0;
  });

  const actions = document.createElement("div");
  actions.className = "row";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A criar…" : "Criar cliente";
  submit.disabled = busy;
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "ghost";
  cancel.textContent = "Cancelar";
  cancel.disabled = busy;
  cancel.addEventListener("click", () => {
    void withBusy(async () => {
      await openClientes();
    });
  });
  actions.append(submit, cancel);

  form.append(nameLabel, slugLabel, actions);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const data = new FormData(form);
    const name = formString(data, "name").trim();
    const slug = formString(data, "slug").trim();
    void withBusy(async () => {
      const created = await createClient({ name, slug });
      catalog = null;
      lastSearchScreen = initialSearchScreen();
      await openClient(created.id, name);
    });
  });
  wrap.append(form);
  return wrap;
};

const renderEditClient = (client: ClientDetail): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Editar cliente";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent = "Atualiza o nome do cliente na organização ativa.";
  wrap.append(heading, intro);

  const form = document.createElement("form");
  form.className = "stack";
  form.setAttribute("data-edit-client", "true");

  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Nome";
  const nameInput = document.createElement("input");
  nameInput.name = "name";
  nameInput.type = "text";
  nameInput.required = true;
  nameInput.minLength = 2;
  nameInput.maxLength = 160;
  nameInput.value = client.name;
  nameInput.autocomplete = "organization";
  nameLabel.append(nameInput);

  const actions = document.createElement("div");
  actions.className = "row";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A guardar…" : "Guardar";
  submit.disabled = busy;
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "ghost";
  cancel.textContent = "Cancelar";
  cancel.disabled = busy;
  cancel.addEventListener("click", () => {
    void withBusy(async () => {
      await openClient(client.id, client.name);
    });
  });
  actions.append(submit, cancel);

  form.append(nameLabel, actions);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const data = new FormData(form);
    const name = formString(data, "name").trim();
    void withBusy(async () => {
      await updateClient({
        clientId: client.id,
        name,
        expectedVersion: client.version,
      });
      catalog = null;
      lastSearchScreen = initialSearchScreen();
      await openClient(client.id, name);
    });
  });
  wrap.append(form);
  return wrap;
};

const activeMembersForCreate = (
  members: readonly MembershipSummary[],
): readonly MembershipSummary[] =>
  members.filter((member) => member.status === undefined || member.status === "ACTIVE");

const openCreateWebsite = async (preselectedClientId: string | null): Promise<void> => {
  const [clients, members] = await Promise.all([listAllClients(), listMembers()]);
  screen = {
    name: "create-website",
    clients,
    members: activeMembersForCreate(members),
    preselectedClientId,
  };
};

const renderCreateWebsite = (state: {
  readonly clients: readonly ClientSummary[];
  readonly members: readonly MembershipSummary[];
  readonly preselectedClientId: string | null;
}): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Novo website";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent = "Cria um website na organização ativa, associado a um cliente existente.";
  wrap.append(heading, intro);

  if (state.clients.length === 0) {
    wrap.append(mutedNote("Não há clientes ativos. Cria um cliente antes de registar um website."));
    const back = document.createElement("button");
    back.type = "button";
    back.className = "ghost";
    back.textContent = "Voltar";
    back.addEventListener("click", () => {
      void withBusy(async () => {
        await openClientes();
      });
    });
    wrap.append(back);
    return wrap;
  }

  if (state.members.length === 0) {
    wrap.append(mutedNote("Não há membros ativos para atribuir como responsável."));
    const back = document.createElement("button");
    back.type = "button";
    back.className = "ghost";
    back.textContent = "Voltar";
    back.addEventListener("click", () => {
      void withBusy(async () => {
        await openClientes();
      });
    });
    wrap.append(back);
    return wrap;
  }

  const form = document.createElement("form");
  form.className = "stack";

  const clientLabel = document.createElement("label");
  clientLabel.textContent = "Cliente";
  const clientSelect = document.createElement("select");
  clientSelect.name = "clientId";
  clientSelect.required = true;
  for (const client of state.clients) {
    const option = document.createElement("option");
    option.value = client.id;
    option.textContent = client.name;
    if (state.preselectedClientId === client.id) {
      option.selected = true;
    }
    clientSelect.append(option);
  }
  if (
    state.preselectedClientId !== null &&
    !state.clients.some((client) => client.id === state.preselectedClientId)
  ) {
    clientSelect.value = state.clients[0]?.id ?? "";
  }
  clientLabel.append(clientSelect);

  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Nome";
  const nameInput = document.createElement("input");
  nameInput.name = "name";
  nameInput.type = "text";
  nameInput.required = true;
  nameInput.minLength = 2;
  nameInput.maxLength = 160;
  nameInput.placeholder = "Nome do website";
  nameInput.autocomplete = "off";
  nameLabel.append(nameInput);

  const domainLabel = document.createElement("label");
  domainLabel.textContent = "Domínio";
  const domainInput = document.createElement("input");
  domainInput.name = "domain";
  domainInput.type = "text";
  domainInput.required = true;
  domainInput.minLength = 1;
  domainInput.maxLength = 253;
  domainInput.placeholder = "exemplo.com";
  domainInput.autocomplete = "off";
  domainLabel.append(domainInput);

  const ownerLabel = document.createElement("label");
  ownerLabel.textContent = "Responsável";
  const ownerSelect = document.createElement("select");
  ownerSelect.name = "ownerMembershipId";
  ownerSelect.required = true;
  for (const member of state.members) {
    const option = document.createElement("option");
    option.value = member.id;
    option.textContent = member.displayName;
    ownerSelect.append(option);
  }
  ownerLabel.append(ownerSelect);

  const environmentLabel = document.createElement("label");
  environmentLabel.textContent = "Ambiente";
  const environmentSelect = document.createElement("select");
  environmentSelect.name = "environment";
  environmentSelect.required = true;
  for (const environment of ["PRODUCTION", "STAGING"] as const) {
    const option = document.createElement("option");
    option.value = environment;
    option.textContent = formatWebsiteEnvironment(environment);
    if (environment === "PRODUCTION") {
      option.selected = true;
    }
    environmentSelect.append(option);
  }
  environmentLabel.append(environmentSelect);

  const actions = document.createElement("div");
  actions.className = "row";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A criar…" : "Criar website";
  submit.disabled = busy;
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "ghost";
  cancel.textContent = "Cancelar";
  cancel.disabled = busy;
  cancel.addEventListener("click", () => {
    void withBusy(async () => {
      if (state.preselectedClientId !== null) {
        await openClient(state.preselectedClientId, null);
        return;
      }
      await openClientes();
    });
  });
  actions.append(submit, cancel);

  form.append(clientLabel, nameLabel, domainLabel, ownerLabel, environmentLabel, actions);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const data = new FormData(form);
    const clientId = formString(data, "clientId").trim();
    const name = formString(data, "name").trim();
    const domain = formString(data, "domain").trim();
    const ownerMembershipId = formString(data, "ownerMembershipId").trim();
    const environment = formString(data, "environment").trim() as WebsiteEnvironment;
    void withBusy(async () => {
      const created = await createWebsite({
        clientId,
        name,
        domain,
        ownerMembershipId,
        environment,
      });
      catalog = null;
      lastSearchScreen = initialSearchScreen();
      await openWebsiteByIds(clientId, created.id);
    });
  });
  wrap.append(form);
  return wrap;
};

const renderEditWebsite = (client: ClientDetail, website: WebsiteDetail): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Editar website";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent = "Atualiza o nome e o domínio do website na organização ativa.";
  wrap.append(heading, intro);

  const form = document.createElement("form");
  form.className = "stack";
  form.setAttribute("data-edit-website", "true");

  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Nome";
  const nameInput = document.createElement("input");
  nameInput.name = "name";
  nameInput.type = "text";
  nameInput.required = true;
  nameInput.minLength = 2;
  nameInput.maxLength = 160;
  nameInput.value = website.name;
  nameInput.autocomplete = "off";
  nameLabel.append(nameInput);

  const domainLabel = document.createElement("label");
  domainLabel.textContent = "Domínio";
  const domainInput = document.createElement("input");
  domainInput.name = "domain";
  domainInput.type = "text";
  domainInput.required = true;
  domainInput.minLength = 1;
  domainInput.maxLength = 253;
  domainInput.value = website.domain;
  domainInput.autocomplete = "off";
  domainLabel.append(domainInput);

  const actions = document.createElement("div");
  actions.className = "row";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A guardar…" : "Guardar";
  submit.disabled = busy;
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "ghost";
  cancel.textContent = "Cancelar";
  cancel.disabled = busy;
  cancel.addEventListener("click", () => {
    void withBusy(async () => {
      await openWebsiteByIds(client.id, website.id);
    });
  });
  actions.append(submit, cancel);

  form.append(nameLabel, domainLabel, actions);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const data = new FormData(form);
    const name = formString(data, "name").trim();
    const domain = formString(data, "domain").trim();
    void withBusy(async () => {
      await updateWebsite({
        websiteId: website.id,
        name,
        domain,
        expectedVersion: website.version,
      });
      catalog = null;
      lastSearchScreen = initialSearchScreen();
      await openWebsiteByIds(client.id, website.id);
    });
  });
  wrap.append(form);
  return wrap;
};

const projectCreateContextFromScreen = (): {
  readonly clientId: string | null;
  readonly websiteId: string | null;
} => {
  if (screen.name === "website") {
    return { clientId: screen.client.id, websiteId: screen.website.id };
  }
  if (screen.name === "client") {
    return { clientId: screen.client.id, websiteId: null };
  }
  if (screen.name === "project") {
    return {
      clientId: screen.client.id,
      websiteId: screen.linkedWebsite?.id ?? null,
    };
  }
  return { clientId: null, websiteId: null };
};

const openCreateProject = async (
  preselectedClientId: string | null,
  preselectedWebsiteId: string | null,
): Promise<void> => {
  const [clients, websites, members] = await Promise.all([
    listAllClients(),
    listAllWebsites(),
    listMembers(),
  ]);
  let clientId = preselectedClientId;
  if (clientId === null && preselectedWebsiteId !== null) {
    clientId = websites.find((website) => website.id === preselectedWebsiteId)?.clientId ?? null;
  }
  screen = {
    name: "create-project",
    clients,
    websites,
    members: activeMembersForCreate(members),
    preselectedClientId: clientId,
    preselectedWebsiteId,
  };
};

const renderCreateProject = (state: {
  readonly clients: readonly ClientSummary[];
  readonly websites: readonly WebsiteSummary[];
  readonly members: readonly MembershipSummary[];
  readonly preselectedClientId: string | null;
  readonly preselectedWebsiteId: string | null;
}): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Novo projeto";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent =
    state.preselectedWebsiteId !== null
      ? "Cria um projeto na organização ativa e associa-o ao website selecionado."
      : "Cria um projeto na organização ativa, associado a um cliente. Opcionalmente liga a um website.";
  wrap.append(heading, intro);

  if (state.clients.length === 0) {
    wrap.append(mutedNote("Não há clientes ativos. Cria um cliente antes de registar um projeto."));
    const back = document.createElement("button");
    back.type = "button";
    back.className = "ghost";
    back.textContent = "Voltar";
    back.addEventListener("click", () => {
      void withBusy(async () => {
        await openClientes();
      });
    });
    wrap.append(back);
    return wrap;
  }

  if (state.members.length === 0) {
    wrap.append(mutedNote("Não há membros ativos para atribuir como responsável."));
    const back = document.createElement("button");
    back.type = "button";
    back.className = "ghost";
    back.textContent = "Voltar";
    back.addEventListener("click", () => {
      void withBusy(async () => {
        await openClientes();
      });
    });
    wrap.append(back);
    return wrap;
  }

  const form = document.createElement("form");
  form.className = "stack";

  const clientLabel = document.createElement("label");
  clientLabel.textContent = "Cliente";
  const clientSelect = document.createElement("select");
  clientSelect.name = "clientId";
  clientSelect.required = true;
  for (const client of state.clients) {
    const option = document.createElement("option");
    option.value = client.id;
    option.textContent = client.name;
    if (state.preselectedClientId === client.id) {
      option.selected = true;
    }
    clientSelect.append(option);
  }
  if (
    state.preselectedClientId !== null &&
    !state.clients.some((client) => client.id === state.preselectedClientId)
  ) {
    clientSelect.value = state.clients[0]?.id ?? "";
  }
  clientLabel.append(clientSelect);

  const websiteLabel = document.createElement("label");
  websiteLabel.textContent = state.preselectedWebsiteId !== null ? "Website" : "Website (opcional)";
  const websiteSelect = document.createElement("select");
  websiteSelect.name = "websiteId";
  const noneOption = document.createElement("option");
  noneOption.value = "";
  noneOption.textContent = "Sem website";
  if (state.preselectedWebsiteId === null) {
    websiteSelect.append(noneOption);
  }

  const fillWebsiteOptions = (clientId: string): void => {
    const previous = websiteSelect.value;
    while (websiteSelect.options.length > 0) {
      websiteSelect.remove(0);
    }
    if (state.preselectedWebsiteId === null) {
      const empty = document.createElement("option");
      empty.value = "";
      empty.textContent = "Sem website";
      websiteSelect.append(empty);
    }
    const forClient = state.websites.filter((website) => website.clientId === clientId);
    for (const website of forClient) {
      const option = document.createElement("option");
      option.value = website.id;
      option.textContent = website.domain;
      if (state.preselectedWebsiteId === website.id) {
        option.selected = true;
      }
      websiteSelect.append(option);
    }
    if (state.preselectedWebsiteId !== null) {
      websiteSelect.required = true;
      const locked = forClient.find((website) => website.id === state.preselectedWebsiteId);
      if (locked === undefined) {
        websiteSelect.value = forClient[0]?.id ?? "";
      }
    } else if (previous !== "" && forClient.some((website) => website.id === previous)) {
      websiteSelect.value = previous;
    }
  };

  fillWebsiteOptions(clientSelect.value);
  clientSelect.addEventListener("change", () => {
    fillWebsiteOptions(clientSelect.value);
  });
  websiteLabel.append(websiteSelect);

  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Nome";
  const nameInput = document.createElement("input");
  nameInput.name = "name";
  nameInput.type = "text";
  nameInput.required = true;
  nameInput.minLength = 2;
  nameInput.maxLength = 160;
  nameInput.placeholder = "Nome do projeto";
  nameInput.autocomplete = "off";
  nameLabel.append(nameInput);

  const ownerLabel = document.createElement("label");
  ownerLabel.textContent = "Responsável";
  const ownerSelect = document.createElement("select");
  ownerSelect.name = "ownerMembershipId";
  ownerSelect.required = true;
  for (const member of state.members) {
    const option = document.createElement("option");
    option.value = member.id;
    option.textContent = member.displayName;
    ownerSelect.append(option);
  }
  ownerLabel.append(ownerSelect);

  const actions = document.createElement("div");
  actions.className = "row";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A criar…" : "Criar projeto";
  submit.disabled = busy;
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "ghost";
  cancel.textContent = "Cancelar";
  cancel.disabled = busy;
  cancel.addEventListener("click", () => {
    void withBusy(async () => {
      if (state.preselectedWebsiteId !== null && state.preselectedClientId !== null) {
        await openWebsiteByIds(state.preselectedClientId, state.preselectedWebsiteId);
        return;
      }
      if (state.preselectedClientId !== null) {
        await openClient(state.preselectedClientId, null);
        return;
      }
      await openClientes();
    });
  });
  actions.append(submit, cancel);

  form.append(clientLabel, websiteLabel, nameLabel, ownerLabel, actions);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const data = new FormData(form);
    const clientId = formString(data, "clientId").trim();
    const websiteId = formString(data, "websiteId").trim();
    const name = formString(data, "name").trim();
    const ownerMembershipId = formString(data, "ownerMembershipId").trim();
    void withBusy(async () => {
      if (websiteId.length > 0) {
        const website = state.websites.find((item) => item.id === websiteId);
        if (website?.clientId !== clientId) {
          throw new ApiRequestError(
            400,
            "VALIDATION_ERROR",
            "Website inválido para o cliente selecionado.",
          );
        }
      }
      const created = await createProject({
        clientId,
        name,
        ownerMembershipId,
      });
      if (websiteId.length > 0) {
        let expectedVersion = state.websites.find((item) => item.id === websiteId)?.version ?? null;
        if (expectedVersion === null) {
          const detail = await getWebsite(websiteId);
          expectedVersion = detail.version;
        }
        await linkWebsiteToProject({
          websiteId,
          projectId: created.id,
          expectedVersion,
        });
      }
      catalog = null;
      lastSearchScreen = initialSearchScreen();
      await openProjectByIds(clientId, created.id);
    });
  });
  wrap.append(form);
  return wrap;
};

const renderEditProject = (client: ClientDetail, project: ProjectDetail): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Editar projeto";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent = "Atualiza o nome do projeto na organização ativa.";
  wrap.append(heading, intro);

  const form = document.createElement("form");
  form.className = "stack";
  form.setAttribute("data-edit-project", "true");

  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Nome";
  const nameInput = document.createElement("input");
  nameInput.name = "name";
  nameInput.type = "text";
  nameInput.required = true;
  nameInput.minLength = 2;
  nameInput.maxLength = 160;
  nameInput.value = project.name;
  nameInput.autocomplete = "off";
  nameLabel.append(nameInput);

  const actions = document.createElement("div");
  actions.className = "row";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A guardar…" : "Guardar";
  submit.disabled = busy;
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "ghost";
  cancel.textContent = "Cancelar";
  cancel.disabled = busy;
  cancel.addEventListener("click", () => {
    void withBusy(async () => {
      await openProjectByIds(client.id, project.id);
    });
  });
  actions.append(submit, cancel);

  form.append(nameLabel, actions);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const data = new FormData(form);
    const name = formString(data, "name").trim();
    void withBusy(async () => {
      await updateProject({
        projectId: project.id,
        name,
        expectedVersion: project.version,
      });
      catalog = null;
      lastSearchScreen = initialSearchScreen();
      await openProjectByIds(client.id, project.id);
    });
  });
  wrap.append(form);
  return wrap;
};

const deniedList = async <T>(
  load: () => Promise<readonly T[]>,
): Promise<{ readonly items: readonly T[]; readonly denied: boolean }> => {
  try {
    return { items: await load(), denied: false };
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 403) {
      return { items: [], denied: true };
    }
    throw error;
  }
};

const loadCatalog = async (): Promise<LoadedCatalog> => {
  const [clientsOutcome, websitesOutcome, projectsOutcome] = await Promise.all([
    deniedList(listAllClients),
    deniedList(listAllWebsites),
    deniedList(listAllProjects),
  ]);
  return {
    clients: clientsOutcome.items,
    websites: websitesOutcome.items,
    projects: projectsOutcome.items,
    clientReadDenied: clientsOutcome.denied,
    websiteReadDenied: websitesOutcome.denied,
    projectReadDenied: projectsOutcome.denied,
  };
};

const loadAndShowDashboard = async (): Promise<void> => {
  screen = { name: "dashboard" };
  catalog = await loadCatalog();
};

const openClientes = async (): Promise<void> => {
  clientScreenStash = null;
  catalog ??= await loadCatalog();
  screen = lastSearchScreen.query.length > 0 ? lastSearchScreen : initialSearchScreen();
};

const openWebsitesOrg = async (): Promise<void> => {
  catalog ??= await loadCatalog();
  screen = {
    name: "websites",
    items: catalog.websites,
    denied: catalog.websiteReadDenied,
  };
};

const openProjectsOrg = async (): Promise<void> => {
  catalog ??= await loadCatalog();
  screen = {
    name: "projects",
    items: catalog.projects,
    denied: catalog.projectReadDenied,
  };
};

const showClientWebsitesTab = (): boolean => {
  if (screen.name !== "client") {
    return false;
  }
  const next = { ...screen, tab: "websites" as const };
  clientScreenStash = next;
  screen = next;
  return true;
};

const goWebsites = async (): Promise<void> => {
  if (screen.name === "client") {
    showClientWebsitesTab();
    return;
  }
  if (screen.name === "website" || screen.name === "project") {
    const clientId = screen.client.id;
    const clientName = screen.client.name;
    restoreClientScreen(clientId);
    if (showClientWebsitesTab()) {
      return;
    }
    await openClient(clientId, clientName);
    showClientWebsitesTab();
    return;
  }
  await openWebsitesOrg();
};

const goProjetos = async (): Promise<void> => {
  if (screen.name === "client") {
    const projectId = resolvedProjectId(screen);
    if (projectId !== null) {
      stashClientScreen(screen);
      const detail =
        screen.projectDetail !== null && screen.projectDetail.id === projectId
          ? screen.projectDetail
          : await getProject(projectId);
      let linked: WebsiteSummary | undefined;
      if (screen.primary.kind === "known" && screen.primary.website.projectId === projectId) {
        const primaryId = screen.primary.website.id;
        linked = screen.websites.find((website) => website.id === primaryId);
      }
      await enterProjectScreen({
        client: screen.client,
        project: detail,
        members: screen.members,
        ...(linked !== undefined ? { linkedWebsite: linked } : {}),
      });
      return;
    }
    return;
  }
  if (screen.name === "website") {
    const projectId = resolvedWebsiteProjectId(
      screen.active,
      screen.projectDetail,
      screen.chooserPick,
    );
    if (projectId !== null) {
      const detail =
        screen.projectDetail !== null && screen.projectDetail.id === projectId
          ? screen.projectDetail
          : await getProject(projectId);
      await enterProjectScreen({
        client: screen.client,
        project: detail,
        members: screen.members,
        linkedWebsite: screen.website,
      });
      return;
    }
    return;
  }
  if (screen.name === "project") {
    return;
  }
  await openProjectsOrg();
};

const renderDashboard = (): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack dash-stack";
  wrap.append(renderDashboardHero());

  if (busy && catalog === null) {
    wrap.append(renderDashboardSkeleton());
    return wrap;
  }

  const loaded = catalog;
  if (loaded === null) {
    wrap.append(
      emptyState(
        iconInbox(),
        "Ainda sem dados",
        "Não foi possível carregar as listas da organização.",
        {
          label: "Tentar de novo",
          onClick: () => {
            void withBusy(async () => {
              await loadAndShowDashboard();
            });
          },
        },
      ),
    );
    return wrap;
  }

  wrap.append(renderStats(loaded));

  const grid = document.createElement("div");
  grid.className = "dash-grid";
  grid.append(
    renderLastClients(loaded),
    renderRecentProjects(loaded),
    renderRecentWebsites(loaded),
    renderDashboardTimeline(loaded),
  );
  const upcoming = upcomingFromProjects(loaded.projects, 5);
  if (upcoming.length > 0) {
    const upcomingCard = renderUpcoming(upcoming);
    upcomingCard.classList.add("dash-span");
    grid.append(upcomingCard);
  }
  wrap.append(grid);
  return wrap;
};

const renderDashboardHero = (): HTMLElement => {
  const hero = document.createElement("div");
  hero.className = "dash-hero";
  const copy = document.createElement("div");
  copy.className = "dash-hero-copy";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = greetingForHour(new Date().getHours());
  const lead = document.createElement("p");
  lead.className = "page-lead";
  lead.textContent =
    organizationName === null
      ? "Estado da agência — clientes, websites e projetos."
      : `${organizationName} · estado agora.`;
  copy.append(heading, lead);
  const search = document.createElement("button");
  search.type = "button";
  search.className = "dash-search";
  search.disabled = busy;
  search.setAttribute("aria-keyshortcuts", "Control+K Meta+K");
  search.innerHTML = `<span class="icon">${iconSearch()}</span><span class="dash-search-label">Pesquisar clientes, websites e projetos</span><span class="nav-hint">Ctrl+K</span>`;
  search.addEventListener("click", () => {
    openPalette();
  });
  hero.append(copy, search);
  return hero;
};

const renderDashboardSkeleton = (): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  wrap.setAttribute("aria-hidden", "true");
  const stats = document.createElement("div");
  stats.className = "stats";
  for (let index = 0; index < 4; index += 1) {
    const card = document.createElement("div");
    card.className = "stat";
    card.innerHTML = `<span class="skeleton sk-title"></span><span class="skeleton sk-value"></span>`;
    stats.append(card);
  }
  const grid = document.createElement("div");
  grid.className = "dash-grid";
  grid.append(
    skeletonCard("Últimos clientes", true),
    skeletonCard("Projetos recentes", true),
    skeletonCard("Websites recentes", true),
    skeletonTimelineCard(),
  );
  wrap.append(stats, grid);
  return wrap;
};

const skeletonCard = (title: string, withList: boolean): HTMLElement => {
  const card = sectionCard(iconInbox(), title);
  if (withList) {
    card.append(skeletonList(4));
  }
  return card;
};

const skeletonList = (rows: number): HTMLElement => {
  const list = document.createElement("ul");
  list.className = "list";
  for (let index = 0; index < rows; index += 1) {
    const li = document.createElement("li");
    li.className = "item-skeleton";
    li.innerHTML = `<span class="skeleton avatar-sk"></span><span class="sk-col"><span class="skeleton sk-title"></span><span class="skeleton sk-line"></span></span>`;
    list.append(li);
  }
  return list;
};

const skeletonTimelineCard = (): HTMLElement => {
  const card = sectionCard(iconActivity(), "Atividade");
  const list = document.createElement("ol");
  list.className = "timeline timeline-sk";
  for (let index = 0; index < 4; index += 1) {
    const li = document.createElement("li");
    li.innerHTML = `<span class="skeleton dot-sk"></span><span class="sk-col"><span class="skeleton sk-title"></span><span class="skeleton sk-line"></span></span>`;
    list.append(li);
  }
  card.append(list);
  return card;
};

const renderStats = (loaded: LoadedCatalog): HTMLElement => {
  const updates7d = countUpdatedWithinDays(
    loaded.clientReadDenied ? [] : loaded.clients,
    loaded.websiteReadDenied ? [] : loaded.websites,
    loaded.projectReadDenied ? [] : loaded.projects,
    7,
  );
  const deniedAll = loaded.clientReadDenied && loaded.websiteReadDenied && loaded.projectReadDenied;
  const stats = document.createElement("div");
  stats.className = "stats";
  const cards: readonly {
    readonly label: string;
    readonly value: string;
    readonly note: string | null;
    readonly icon: string;
    readonly onClick: (() => void) | null;
  }[] = [
    {
      label: "Clientes",
      value: loaded.clientReadDenied ? "—" : String(loaded.clients.length),
      note: loaded.clientReadDenied ? "Sem permissão" : null,
      icon: iconClients(),
      onClick: (): void => {
        void withBusy(async () => {
          await openClientes();
        });
      },
    },
    {
      label: "Websites",
      value: loaded.websiteReadDenied ? "—" : String(loaded.websites.length),
      note: loaded.websiteReadDenied ? "Sem permissão" : null,
      icon: iconGlobe(),
      onClick: (): void => {
        void withBusy(async () => {
          await openWebsitesOrg();
        });
      },
    },
    {
      label: "Projetos",
      value: loaded.projectReadDenied ? "—" : String(loaded.projects.length),
      note: loaded.projectReadDenied ? "Sem permissão" : null,
      icon: iconFolder(),
      onClick: (): void => {
        void withBusy(async () => {
          await openProjectsOrg();
        });
      },
    },
    {
      label: "Atualizações",
      value: deniedAll ? "—" : String(updates7d),
      note: deniedAll ? "Sem permissão" : "Últimos 7 dias",
      icon: iconActivity(),
      onClick: null,
    },
  ];
  for (const card of cards) {
    const el = document.createElement(card.onClick === null ? "div" : "button");
    if (el instanceof HTMLButtonElement) {
      el.type = "button";
      el.disabled = busy;
      el.addEventListener("click", () => {
        card.onClick?.();
      });
    }
    el.className = "stat";
    el.innerHTML = `<p class="stat-label"><span class="icon">${card.icon}</span>${escapeHtml(
      card.label,
    )}</p><p class="stat-value">${escapeHtml(card.value)}</p>${
      card.note === null ? "" : `<p class="stat-note">${escapeHtml(card.note)}</p>`
    }`;
    stats.append(el);
  }
  return stats;
};

const renderLastClients = (loaded: LoadedCatalog): HTMLElement => {
  const card = sectionCard(iconClients(), "Últimos clientes");
  if (loaded.clientReadDenied) {
    card.append(mutedNote("Sem permissão para ver clientes."));
    return card;
  }
  const items = latestByUpdated(loaded.clients, 5);
  if (items.length === 0) {
    card.append(
      emptyState(
        iconClients(),
        "Ainda sem clientes",
        "Quando existirem clientes nesta organização, os mais recentes aparecem aqui.",
      ),
    );
    return card;
  }
  const list = document.createElement("ul");
  list.className = "list";
  for (const client of items) {
    list.append(clientResultItem(client));
  }
  card.append(list);
  return card;
};

const renderRecentProjects = (loaded: LoadedCatalog): HTMLElement => {
  const card = sectionCard(iconFolder(), "Projetos recentes");
  if (loaded.projectReadDenied) {
    card.append(mutedNote("Sem permissão para ver projetos."));
    return card;
  }
  const items = latestByUpdated(loaded.projects, 5);
  if (items.length === 0) {
    card.append(
      emptyState(
        iconFolder(),
        "Ainda sem projetos",
        "Os projetos da organização ativa aparecem aqui quando existirem.",
      ),
    );
    return card;
  }
  const list = document.createElement("ul");
  list.className = "list";
  for (const project of items) {
    const li = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "item item-row";
    button.disabled = busy;
    const badgeClass = isPositiveProjectStatus(project.status) ? "badge" : "badge inactive";
    const clientName = catalog?.clients.find((client) => client.id === project.clientId)?.name;
    const owner = clientName === undefined ? "" : ` · ${escapeHtml(clientName)}`;
    button.innerHTML = `<span class="avatar sm" aria-hidden="true">${escapeHtml(initialsFromName(project.name))}</span><span class="item-body"><strong>${escapeHtml(project.name)}</strong><span class="muted"><span class="${badgeClass}">${escapeHtml(formatProjectStatus(project.status))}</span>${owner}</span></span>`;
    button.addEventListener("click", () => {
      void withBusy(async () => {
        await openProjectByIds(project.clientId, project.id);
      });
    });
    li.append(button);
    list.append(li);
  }
  card.append(list);
  return card;
};

const renderRecentWebsites = (loaded: LoadedCatalog): HTMLElement => {
  const card = sectionCard(iconGlobe(), "Websites recentes");
  if (loaded.websiteReadDenied) {
    card.append(mutedNote("Sem permissão para ver websites."));
    return card;
  }
  const items = latestByUpdated(loaded.websites, 5);
  if (items.length === 0) {
    card.append(
      emptyState(
        iconGlobe(),
        "Ainda sem websites",
        "Os websites da organização ativa aparecem aqui quando existirem.",
      ),
    );
    return card;
  }
  const list = document.createElement("ul");
  list.className = "list";
  for (const website of items) {
    const li = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "item item-row";
    button.disabled = busy;
    const badgeClass = isPositiveWebsiteStatus(website.status) ? "badge" : "badge inactive";
    const clientName = catalog?.clients.find((client) => client.id === website.clientId)?.name;
    const owner = clientName === undefined ? "" : ` · ${escapeHtml(clientName)}`;
    button.innerHTML = `<span class="avatar sm" aria-hidden="true">${escapeHtml(initialsFromName(website.domain))}</span><span class="item-body"><strong>${escapeHtml(website.domain)}</strong><span class="muted"><span class="${badgeClass}">${escapeHtml(formatWebsiteStatus(website.status))}</span>${owner}</span></span>`;
    button.addEventListener("click", () => {
      void withBusy(async () => {
        await openWebsiteByIds(website.clientId, website.id);
      });
    });
    li.append(button);
    list.append(li);
  }
  card.append(list);
  return card;
};

const renderDashboardTimeline = (loaded: LoadedCatalog): HTMLElement => {
  const activity = recentActivity(
    loaded.clientReadDenied ? [] : loaded.clients,
    loaded.websiteReadDenied ? [] : loaded.websites,
    loaded.projectReadDenied ? [] : loaded.projects,
    8,
  );
  const card = sectionCard(iconActivity(), "Atividade");
  if (activity.length === 0) {
    card.append(
      emptyState(
        iconActivity(),
        "Ainda sem atividade",
        "Quando houver clientes, websites ou projetos, as atualizações aparecem aqui.",
        {
          label: "Ver clientes",
          onClick: () => {
            void withBusy(async () => {
              await openClientes();
            });
          },
        },
      ),
    );
    return card;
  }
  const timelineItems: TimelineItem[] = activity.map((item) => ({
    id: item.id,
    at: item.at,
    title: item.title,
    detail: activityKindLabel(item.kind),
    kind: "updated",
    actor: null,
  }));
  const byId = new Map(activity.map((item) => [item.id, item]));
  const groups = groupTimelineByDay(timelineItems, new Date(), relativeDayLabel);
  for (const group of groups) {
    const block = document.createElement("div");
    block.className = "timeline-group";
    const heading = document.createElement("p");
    heading.className = "timeline-day";
    heading.textContent = group.label;
    const list = document.createElement("ol");
    list.className = "timeline";
    for (const item of group.items) {
      const source = byId.get(item.id);
      const li = document.createElement("li");
      if (source === undefined) {
        const staticRow = document.createElement("div");
        staticRow.className = "timeline-static";
        staticRow.append(timelineStaticBody(item));
        li.append(staticRow);
        list.append(li);
        continue;
      }
      const button = document.createElement("button");
      button.type = "button";
      button.className = "timeline-hit";
      button.disabled = busy;
      button.append(timelineStaticBody(item));
      button.addEventListener("click", () => {
        void withBusy(
          async () => {
            await openActivity(source);
          },
          source.kind === "client" ? { freezeShell: false, skipInitialRender: true } : undefined,
        );
      });
      li.append(button);
      list.append(li);
    }
    block.append(heading, list);
    card.append(block);
  }
  return card;
};

const timelineStaticBody = (item: TimelineItem): DocumentFragment => {
  const fragment = document.createDocumentFragment();
  const mark = document.createElement("span");
  mark.className = item.kind === "deploy" ? "dot deploy" : "dot";
  mark.innerHTML = `<span class="icon timeline-icon">${timelineKindIcon(item.kind)}</span>`;
  const body = document.createElement("div");
  const when = document.createElement("p");
  when.className = "when";
  when.textContent = item.at === null ? "Sem data" : formatDateTime(item.at);
  const what = document.createElement("p");
  what.className = "what";
  what.textContent = item.title;
  body.append(when, what);
  if (item.actor !== null) {
    const actor = document.createElement("p");
    actor.className = "detail actor";
    actor.textContent = item.actor;
    body.append(actor);
  }
  if (item.detail !== null) {
    const detail = document.createElement("p");
    detail.className = "detail";
    detail.textContent = item.detail;
    body.append(detail);
  }
  fragment.append(mark, body);
  return fragment;
};

const timelineKindIcon = (kind: TimelineItem["kind"]): string => {
  switch (kind) {
    case "created":
      return iconPlus();
    case "updated":
      return iconActivity();
    case "opened":
      return iconUser();
    case "deploy":
      return iconSpark();
  }
};

const activityKindLabel = (kind: ActivityItem["kind"]): string => {
  switch (kind) {
    case "client":
      return "Cliente";
    case "website":
      return "Website";
    case "project":
      return "Projeto";
  }
};

const openActivity = async (item: ActivityItem): Promise<void> => {
  if (item.kind === "client") {
    await openClient(item.entityId, item.title);
    return;
  }
  if (item.kind === "website") {
    await openWebsiteByIds(item.clientId, item.entityId);
    return;
  }
  await openProjectByIds(item.clientId, item.entityId);
};

const renderUpcoming = (
  items: readonly {
    readonly id: string;
    readonly name: string;
    readonly plannedCompletion: string;
    readonly clientId: string;
  }[],
): HTMLElement => {
  const card = sectionCard(iconCalendar(), "Próximas datas");
  const intro = mutedNote("Prazos de projeto já existentes — não é um módulo de tarefas.");
  card.append(intro);
  const list = document.createElement("ul");
  list.className = "list";
  for (const item of items) {
    const li = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "item";
    button.disabled = busy;
    button.innerHTML = `<strong>${escapeHtml(item.name)}</strong><span class="muted">Prazo ${escapeHtml(
      formatDate(item.plannedCompletion),
    )}</span>`;
    button.addEventListener("click", () => {
      void withBusy(async () => {
        await openProjectByIds(item.clientId, item.id);
      });
    });
    li.append(button);
    list.append(li);
  }
  card.append(list);
  return card;
};

const renderSearch = (state: SearchScreen): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Clientes";
  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent =
    "Lista da organização ativa. Pesquisa também no servidor (mínimo 2 caracteres).";
  wrap.append(heading, intro);
  const form = document.createElement("form");
  form.className = "row search-row";
  const input = document.createElement("input");
  input.name = "q";
  input.type = "search";
  input.placeholder = "Pesquisar cliente…";
  input.setAttribute("aria-label", "Pesquisar cliente");
  input.style.flex = "1";
  input.value = state.query;
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A pesquisar…" : "Pesquisar";
  submit.disabled = busy;
  form.append(input, submit);

  const listed = state.results ?? catalog?.clients ?? [];
  const denied = state.results === null && (catalog?.clientReadDenied ?? false);

  if (denied) {
    wrap.append(form, mutedNote("Sem permissão para ver clientes."));
    return wrap;
  }

  if (listed.length === 0) {
    wrap.append(
      form,
      emptyState(
        iconClients(),
        state.results !== null ? "Nenhum cliente encontrado" : "Ainda não há clientes",
        state.results !== null
          ? "Tenta outro nome. A pesquisa no servidor exige pelo menos 2 caracteres."
          : "Quando existirem clientes nesta organização, aparecem nesta lista.",
        {
          label: "Ir ao dashboard",
          onClick: () => {
            void withBusy(async () => {
              await loadAndShowDashboard();
            });
          },
        },
      ),
    );
  } else {
    const results = document.createElement("ul");
    results.className = "list";
    for (const client of listed) {
      results.append(clientResultItem(client));
    }
    wrap.append(form, results);
  }

  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const q = input.value.trim();
    if (q.length < 2) {
      screen = { name: "search", query: q, results: null };
      lastSearchScreen = screen;
      render();
      return;
    }
    screen = { name: "search", query: q, results: state.results };
    lastSearchScreen = screen;
    void withBusy(async () => {
      const page = await searchClients(q);
      if (page.items.length === 1) {
        const only = page.items[0];
        if (only !== undefined) {
          lastSearchScreen = { name: "search", query: q, results: page.items };
          await openClient(only.id, only.name);
          return;
        }
      }
      screen = { name: "search", query: q, results: page.items };
      lastSearchScreen = screen;
    });
  });
  return wrap;
};

const clientResultItem = (client: ClientSummary): HTMLElement => {
  const li = document.createElement("li");
  const button = document.createElement("button");
  button.type = "button";
  button.className = "item";
  button.disabled = busy;
  const statusClass = isPositiveClientStatus(client.status) ? "badge" : "badge inactive";
  const contact = [client.email, client.phone]
    .filter((part) => part !== null && part !== "")
    .join(" · ");
  button.className = "item item-row";
  button.innerHTML = `<span class="avatar sm" aria-hidden="true">${escapeHtml(initialsFromName(client.name))}</span><span class="item-body"><strong>${escapeHtml(client.name)}</strong><span class="muted"><span class="${statusClass}">${escapeHtml(formatClientStatus(client.status))}</span>${contact.length > 0 ? ` · ${escapeHtml(contact)}` : ""}</span></span>`;
  button.addEventListener("click", () => {
    void openClientBusy(client.id, client.name);
  });
  li.append(button);
  return li;
};

const renderOrgWebsites = (items: readonly WebsiteSummary[], denied: boolean): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Websites";
  wrap.append(heading);
  if (denied) {
    wrap.append(mutedNote("Sem permissão para ver websites."));
    return wrap;
  }
  if (items.length === 0) {
    wrap.append(
      emptyState(
        iconGlobe(),
        "Ainda não há websites",
        "Os websites aparecem aqui a partir da lista da organização. Abre um cliente para ver o website principal.",
        {
          label: "Ver clientes",
          onClick: () => {
            void withBusy(async () => {
              await openClientes();
            });
          },
        },
      ),
    );
    return wrap;
  }
  const list = document.createElement("ul");
  list.className = "list";
  for (const website of items) {
    const li = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "item";
    button.disabled = busy;
    const clientName = catalog?.clients.find((client) => client.id === website.clientId)?.name;
    const badgeClass = isPositiveWebsiteStatus(website.status) ? "badge" : "badge inactive";
    const owner = clientName === undefined ? "" : ` · ${escapeHtml(clientName)}`;
    button.innerHTML = `<strong>${escapeHtml(website.domain)}</strong><span class="muted"><span class="${badgeClass}">${escapeHtml(formatWebsiteStatus(website.status))}</span> · ${escapeHtml(formatWebsiteEnvironment(website.environment))}${owner}</span><span class="open-label">Abrir website</span>`;
    button.addEventListener("click", () => {
      void withBusy(async () => {
        await openWebsiteByIds(website.clientId, website.id);
      });
    });
    li.append(button);
    list.append(li);
  }
  wrap.append(list);
  return wrap;
};

const renderOrgProjects = (items: readonly ProjectSummary[], denied: boolean): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";
  const heading = document.createElement("h2");
  heading.className = "page-title";
  heading.textContent = "Projetos";
  wrap.append(heading);
  if (denied) {
    wrap.append(mutedNote("Sem permissão para ver projetos."));
    return wrap;
  }
  if (items.length === 0) {
    wrap.append(
      emptyState(
        iconFolder(),
        "Ainda não há projetos",
        "Os projetos da organização ativa aparecem aqui. Cria o primeiro projeto para o acompanhar no workspace.",
        {
          label: "Novo projeto",
          onClick: () => {
            void withBusy(async () => {
              await openCreateProject(null, null);
            });
          },
        },
      ),
    );
    return wrap;
  }
  const createRow = document.createElement("div");
  createRow.className = "row";
  const createButton = document.createElement("button");
  createButton.type = "button";
  createButton.textContent = "Novo projeto";
  createButton.disabled = busy;
  createButton.addEventListener("click", () => {
    void withBusy(async () => {
      await openCreateProject(null, null);
    });
  });
  createRow.append(createButton);
  wrap.append(createRow);
  const list = document.createElement("ul");
  list.className = "list";
  for (const project of items) {
    const li = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "item";
    button.disabled = busy;
    const clientName = catalog?.clients.find((client) => client.id === project.clientId)?.name;
    const badgeClass = isPositiveProjectStatus(project.status) ? "badge" : "badge inactive";
    const owner = clientName === undefined ? "" : ` · ${escapeHtml(clientName)}`;
    const deadline =
      project.plannedCompletion === null
        ? ""
        : ` · prazo ${escapeHtml(formatDate(project.plannedCompletion))}`;
    button.innerHTML = `<strong>${escapeHtml(project.name)}</strong><span class="muted"><span class="${badgeClass}">${escapeHtml(formatProjectStatus(project.status))}</span>${owner}${deadline}</span><span class="open-label">Abrir projeto</span>`;
    button.addEventListener("click", () => {
      void withBusy(async () => {
        await openProjectByIds(project.clientId, project.id);
      });
    });
    li.append(button);
    list.append(li);
  }
  wrap.append(list);
  return wrap;
};

const loadWebsitesSafe = async (
  clientId: string,
): Promise<{
  readonly websites: readonly WebsiteSummary[];
  readonly websiteReadDenied: boolean;
}> => {
  try {
    return { websites: await listWebsitesForClient(clientId), websiteReadDenied: false };
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 403) {
      return { websites: [], websiteReadDenied: true };
    }
    throw error;
  }
};

const loadMembersSafe = async (): Promise<ReadonlyMap<string, string>> => {
  try {
    return memberMap(await listMembers());
  } catch {
    return new Map();
  }
};

const loadProjectsSafe = async (
  clientId: string,
): Promise<{
  readonly projects: readonly ProjectSummary[];
  readonly projectReadDenied: boolean;
}> => {
  try {
    return { projects: await listProjectSummariesForClient(clientId), projectReadDenied: false };
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 403) {
      return { projects: [], projectReadDenied: true };
    }
    throw error;
  }
};

const resolveActiveFromLoaded = async (
  websiteProjectId: string | null,
  projects: readonly ProjectSummary[],
  projectsDenied: boolean,
): Promise<{
  readonly active: ActiveProjectResolution;
  readonly projectDetail: ProjectDetail | null;
  readonly projectReadDenied: boolean;
}> => {
  if (websiteProjectId !== null) {
    try {
      const projectDetail = await getProject(websiteProjectId);
      return {
        active: { kind: "linked", projectId: websiteProjectId },
        projectDetail,
        projectReadDenied: false,
      };
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 403) {
        return { active: { kind: "empty" }, projectDetail: null, projectReadDenied: true };
      }
      return {
        active: activeProjectUnavailable(websiteProjectId),
        projectDetail: null,
        projectReadDenied: false,
      };
    }
  }

  if (projectsDenied) {
    return { active: { kind: "empty" }, projectDetail: null, projectReadDenied: true };
  }

  const listItems = projects.map((item) => ({
    id: item.id,
    status: item.status,
    name: item.name,
    plannedCompletion: item.plannedCompletion,
    ownerMembershipId: item.ownerMembershipId,
    updatedAt: item.updatedAt,
  }));
  const active = resolveActiveProject(null, listItems);
  let projectDetail: ProjectDetail | null = null;
  if (active.kind === "selected") {
    try {
      projectDetail = await getProject(active.project.id);
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 403) {
        return { active: { kind: "empty" }, projectDetail: null, projectReadDenied: true };
      }
      throw error;
    }
  }
  return { active, projectDetail, projectReadDenied: false };
};

const openClient = async (clientId: string, hintName: string | null = null): Promise<void> => {
  screen = { name: "client-loading", clientId, hintName };
  render();

  const [client, websitesOutcome, members, projectsOutcome] = await Promise.all([
    getClient(clientId),
    loadWebsitesSafe(clientId),
    loadMembersSafe(),
    loadProjectsSafe(clientId),
  ]);

  const { websites, websiteReadDenied } = websitesOutcome;
  const { projects, projectReadDenied: projectsListDenied } = projectsOutcome;
  const primary = websiteReadDenied ? ({ kind: "none" } as const) : resolvePrimaryWebsite(websites);

  let active: ActiveProjectResolution | null = null;
  let projectDetail: ProjectDetail | null = null;
  let projectReadDenied = projectsListDenied;

  if (primary.kind === "known") {
    const loaded = await resolveActiveFromLoaded(
      primary.website.projectId,
      projects,
      projectsListDenied,
    );
    active = loaded.active;
    projectDetail = loaded.projectDetail;
    projectReadDenied = loaded.projectReadDenied || projectsListDenied;
  }

  const next: ClientScreen = {
    name: "client",
    client,
    tab: "resumo",
    websites,
    websiteReadDenied,
    primary,
    projects,
    members,
    projectReadDenied,
    active,
    projectDetail,
    projectChooserPick: null,
  };
  clientScreenStash = next;
  screen = next;
};

const openClientBusy = async (clientId: string, hintName: string | null = null): Promise<void> => {
  await withBusy(
    async () => {
      await openClient(clientId, hintName);
    },
    { freezeShell: false, skipInitialRender: true },
  );
};

const openWebsiteByIds = async (clientId: string, websiteId: string): Promise<void> => {
  const [client, members] = await Promise.all([getClient(clientId), loadMembersSafe()]);
  await openWebsiteScreen(client, websiteId, members);
};

const openProjectByIds = async (clientId: string, projectId: string): Promise<void> => {
  const [client, members, project] = await Promise.all([
    getClient(clientId),
    loadMembersSafe(),
    getProject(projectId),
  ]);
  await enterProjectScreen({ client, project, members });
};

const renderClient = (state: ClientScreen): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className =
    state.tab === "resumo" ? "stack client-view client-view-resumo" : "stack client-view";
  wrap.append(renderClientHeader(state), renderClientTabs(state));

  switch (state.tab) {
    case "resumo":
      wrap.append(renderResumo(state));
      break;
    case "websites":
      wrap.append(renderClientWebsitesTab(state));
      break;
  }
  return wrap;
};

const renderClientLoading = (hintName: string | null): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack client-view client-view-resumo";
  const hero = document.createElement("div");
  hero.className = "identity client-hero";
  const avatar = document.createElement("div");
  avatar.className = "avatar";
  avatar.setAttribute("aria-hidden", "true");
  if (hintName !== null) {
    avatar.textContent = initialsFromName(hintName);
  } else {
    avatar.classList.add("avatar-loading");
  }
  const main = document.createElement("div");
  main.className = "identity-main";
  if (hintName !== null) {
    const titleRow = document.createElement("div");
    titleRow.className = "identity-title";
    const heading = document.createElement("h2");
    heading.textContent = hintName;
    titleRow.append(heading);
    main.append(titleRow);
  } else {
    const titleSk = document.createElement("span");
    titleSk.className = "skeleton sk-title";
    main.append(titleSk);
  }
  hero.append(avatar, main);

  const tabs = document.createElement("div");
  tabs.className = "tabs";
  tabs.innerHTML = `<span class="skeleton sk-title" style="width:4.5rem"></span><span class="skeleton sk-title" style="width:5rem"></span>`;

  const resumo = document.createElement("div");
  resumo.className = "stack resumo";
  const cockpitSk = document.createElement("div");
  cockpitSk.className = "resumo-section resumo-cockpit-block";
  const boardSk = document.createElement("div");
  boardSk.className = "situation-board";
  for (let i = 0; i < 3; i += 1) {
    const line = document.createElement("span");
    line.className = "skeleton sk-line";
    line.style.width = i === 0 ? "68%" : i === 1 ? "62%" : "74%";
    boardSk.append(line);
  }
  cockpitSk.append(boardSk);
  const nextSk = document.createElement("div");
  nextSk.className = "resumo-section resumo-next-block";
  const nextTitle = document.createElement("span");
  nextTitle.className = "skeleton sk-title";
  nextTitle.style.width = "7rem";
  const ctaSk = document.createElement("span");
  ctaSk.className = "skeleton sk-title";
  ctaSk.style.width = "8.5rem";
  ctaSk.style.height = "2.35rem";
  nextSk.append(nextTitle, ctaSk);
  const holdingsSk = document.createElement("div");
  holdingsSk.className = "resumo-section resumo-inventory";
  const holdingsTitle = document.createElement("span");
  holdingsTitle.className = "skeleton sk-title";
  holdingsTitle.style.width = "9rem";
  const holdingsLine = document.createElement("span");
  holdingsLine.className = "skeleton sk-line";
  holdingsSk.append(holdingsTitle, holdingsLine);
  resumo.append(cockpitSk, nextSk, holdingsSk);

  wrap.append(hero, tabs, resumo);
  return wrap;
};

const renderClientHeader = (state: ClientScreen): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack client-header-block";
  const identity = document.createElement("div");
  identity.className = "identity client-hero";
  const avatar = document.createElement("div");
  avatar.className = "avatar";
  avatar.setAttribute("aria-hidden", "true");
  avatar.textContent = initialsFromName(state.client.name);
  const main = document.createElement("div");
  main.className = "identity-main";
  const titleRow = document.createElement("div");
  titleRow.className = "identity-title";
  const heading = document.createElement("h2");
  heading.textContent = state.client.name;
  titleRow.append(heading);
  const meta = document.createElement("p");
  meta.className = "identity-meta";
  meta.textContent = formatClientStatus(state.client.status);
  main.append(titleRow, meta);
  identity.append(avatar, main);
  wrap.append(identity);
  if (state.client.status === "ACTIVE") {
    wrap.append(
      renderLifecycleActions({
        onEdit: () => {
          screen = { name: "edit-client", client: state.client };
          render();
        },
        onArchive: () => {
          void withBusy(async () => {
            if (
              !window.confirm(
                `Arquivar o cliente «${state.client.name}»? Sai da lista ativa; websites e projetos ligados não são alterados automaticamente.`,
              )
            ) {
              return;
            }
            await archiveClient({
              clientId: state.client.id,
              expectedVersion: state.client.version,
            });
            catalog = null;
            clientScreenStash = null;
            lastSearchScreen = initialSearchScreen();
            await openClientes();
          });
        },
      }),
    );
  }
  return wrap;
};

const renderLifecycleActions = (input: {
  readonly onEdit: () => void;
  readonly onArchive: () => void;
}): HTMLElement => {
  const row = document.createElement("div");
  row.className = "row entity-lifecycle";
  const edit = document.createElement("button");
  edit.type = "button";
  edit.className = "secondary";
  edit.textContent = "Editar";
  edit.disabled = busy;
  edit.addEventListener("click", () => {
    input.onEdit();
  });
  const archive = document.createElement("button");
  archive.type = "button";
  archive.className = "ghost";
  archive.textContent = "Arquivar";
  archive.disabled = busy;
  archive.addEventListener("click", () => {
    input.onArchive();
  });
  row.append(edit, archive);
  return row;
};

const resolvedProjectForScreen = (state: ClientScreen): ProjectSummary | ProjectDetail | null => {
  const projectId = resolvedProjectId(state);
  if (projectId === null) {
    return null;
  }
  if (state.projectDetail !== null && state.projectDetail.id === projectId) {
    return state.projectDetail;
  }
  return state.projects.find((item) => item.id === projectId) ?? null;
};

const storyInputFromScreen = (state: ClientScreen): ClientStoryInput => {
  const project = resolvedProjectForScreen(state);
  const relative = formatRelativeActivity(
    latestActivityAt(state.client.updatedAt, state.websites, state.projects),
  );
  const lastActivityLabel =
    relative.length > 0 ? `${relative.charAt(0).toLowerCase()}${relative.slice(1)}` : relative;
  return {
    clientStatus: state.client.status,
    websiteReadDenied: state.websiteReadDenied,
    primaryDomain: state.primary.kind === "known" ? state.primary.website.domain : null,
    primaryEnvironment: state.primary.kind === "known" ? state.primary.website.environment : null,
    primaryWebsiteStatus: state.primary.kind === "known" ? state.primary.website.status : null,
    primaryNeedsChoice: state.primary.kind === "chooser",
    projectName: project?.name ?? null,
    projectStatus: project?.status ?? null,
    projectNeedsChoice: state.active?.kind === "chooser",
    projectUnavailable: state.active?.kind === "unavailable",
    projectReadDenied: state.projectReadDenied && state.projects.length === 0,
    responsible: heroResponsible(state),
    lastActivityLabel,
  };
};

const holdingsInputFromScreen = (state: ClientScreen): ClientHoldingsInput => {
  const project = resolvedProjectForScreen(state);
  const primary = state.primary.kind === "known" ? state.primary.website : null;
  return {
    websiteReadDenied: state.websiteReadDenied,
    primaryDomain: primary?.domain ?? null,
    primaryEnvironment: primary?.environment ?? null,
    primaryWebsiteStatus: primary?.status ?? null,
    primaryTechnology: primary?.technology ?? null,
    primaryNeedsChoice: state.primary.kind === "chooser",
    primarySinceLabel: primary !== null ? formatDate(primary.createdAt) : null,
    projectName: project?.name ?? null,
    projectStatus: project?.status ?? null,
    projectNeedsChoice: state.active?.kind === "chooser",
    projectUnavailable: state.active?.kind === "unavailable",
    projectReadDenied: state.projectReadDenied && state.projects.length === 0,
    projectSinceLabel: project !== null ? formatDate(project.createdAt) : null,
    projectDeadlineLabel:
      project?.plannedCompletion !== null && project?.plannedCompletion !== undefined
        ? formatDate(project.plannedCompletion)
        : null,
  };
};

const historyInputFromScreen = (state: ClientScreen): ClientHistoryInput => {
  const project = resolvedProjectForScreen(state);
  const primary = state.primary.kind === "known" ? state.primary.website : null;
  const lastChangeAt = latestActivityAt(state.client.updatedAt, state.websites, state.projects);
  return {
    clientCreatedAt: state.client.createdAt,
    clientCreatedLabel: formatDate(state.client.createdAt),
    websiteCreatedAt: primary?.createdAt ?? null,
    websiteCreatedLabel: primary !== null ? formatDate(primary.createdAt) : null,
    websiteDomain: primary?.domain ?? null,
    websiteEnvironment: primary?.environment ?? null,
    projectCreatedAt: project?.createdAt ?? null,
    projectCreatedLabel: project !== null ? formatDate(project.createdAt) : null,
    projectName: project?.name ?? null,
    lastChangeAt,
    lastChangeLabel: formatChangeWhen(lastChangeAt),
  };
};

const nextAttentionFromScreen = (state: ClientScreen): NextAttention => {
  const projectId = resolvedProjectId(state);
  const project = resolvedProjectForScreen(state);
  const projectDetail =
    state.projectDetail !== null && projectId !== null && state.projectDetail.id === projectId
      ? state.projectDetail
      : null;
  const projectUpdatedRelative: string | null =
    project === null
      ? null
      : ((): string => {
          const relative = formatRelativeActivity(project.updatedAt);
          return relative.length > 0
            ? `${relative.charAt(0).toLowerCase()}${relative.slice(1)}`
            : relative;
        })();
  const projectResponsible =
    project !== null ? responsibleName(project.ownerMembershipId, state.members) : null;
  return composeNextAttention({
    primaryNeedsChoice: state.primary.kind === "chooser",
    projectNeedsChoice: state.active?.kind === "chooser",
    projectUnavailable: state.active?.kind === "unavailable",
    hasOpenableProject: projectId !== null && state.active?.kind !== "chooser",
    hasOpenableWebsite: state.primary.kind === "known",
    primaryDomain: state.primary.kind === "known" ? state.primary.website.domain : null,
    primaryEnvironment: state.primary.kind === "known" ? state.primary.website.environment : null,
    primaryWebsiteStatus: state.primary.kind === "known" ? state.primary.website.status : null,
    projectStatus: project?.status ?? null,
    projectDescription: projectDetail?.description ?? null,
    projectName: project?.name ?? null,
    responsible: projectResponsible ?? heroResponsible(state),
    projectUpdatedRelative,
  });
};

const heroResponsible = (state: ClientScreen): string | null => {
  if (state.primary.kind === "known") {
    return responsibleName(state.primary.website.ownerMembershipId, state.members);
  }
  const projectId = resolvedProjectId(state);
  if (projectId === null) {
    return null;
  }
  const project =
    state.projectDetail !== null && state.projectDetail.id === projectId
      ? state.projectDetail
      : (state.projects.find((item) => item.id === projectId) ?? null);
  if (project === null) {
    return null;
  }
  return responsibleName(project.ownerMembershipId, state.members);
};

const renderClientTabs = (state: ClientScreen): HTMLElement => {
  const tabs = document.createElement("div");
  tabs.className = "tabs";
  tabs.setAttribute("role", "tablist");
  const items: readonly { readonly id: ClientTab; readonly label: string }[] = [
    { id: "resumo", label: "Resumo" },
    { id: "websites", label: "Websites" },
  ];
  for (const item of items) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = state.tab === item.id ? "tab active" : "tab";
    button.setAttribute("role", "tab");
    button.setAttribute("aria-selected", state.tab === item.id ? "true" : "false");
    button.textContent = item.label;
    button.disabled = busy;
    button.addEventListener("click", () => {
      if (screen.name !== "client") {
        return;
      }
      const next = { ...screen, tab: item.id };
      clientScreenStash = next;
      screen = next;
      clearError();
      render();
    });
    tabs.append(button);
  }
  return tabs;
};

const renderResumo = (state: ClientScreen): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack resumo";
  wrap.append(
    renderResumoCockpit(state),
    renderResumoNext(state),
    renderResumoHoldings(state),
    renderResumoHistory(state),
  );
  return wrap;
};

/** 1. Situation cockpit — "Qual é a situação deste cliente?" */
const renderResumoCockpit = (state: ClientScreen): HTMLElement => {
  const section = document.createElement("section");
  section.className = "resumo-section resumo-cockpit-block";
  const board = document.createElement("div");
  board.className = "situation-board";
  board.setAttribute("role", "list");
  for (const row of composeClientSituation(storyInputFromScreen(state)).rows) {
    board.append(renderSituationRow(row));
  }
  section.append(board);
  return section;
};

const renderSituationRow = (row: SituationRow): HTMLElement => {
  const item = document.createElement("div");
  item.className = `situation-row signal-${row.signal}`;
  item.setAttribute("role", "listitem");

  const label = document.createElement("span");
  label.className = "situation-label";
  label.textContent = row.label;

  const signal = document.createElement("span");
  signal.className = `situation-signal signal-${row.signal}`;
  signal.setAttribute("aria-hidden", "true");

  const body = document.createElement("div");
  body.className = "situation-body";
  const phrase = document.createElement("span");
  phrase.className = "situation-phrase";
  phrase.textContent = row.phrase;
  body.append(phrase);
  if (row.detail !== null) {
    const detail = document.createElement("span");
    detail.className = "situation-detail";
    detail.textContent = row.detail;
    body.append(detail);
  }

  item.append(label, signal, body);
  return item;
};

/** 2. Decision block — Situação / Contexto / Ação from real holdings only. */
const renderResumoNext = (state: ClientScreen): HTMLElement => {
  const section = document.createElement("section");
  section.className = "resumo-section resumo-next-block";
  const heading = document.createElement("h3");
  heading.className = "resumo-next-label";
  heading.textContent = "Próxima ação";
  section.append(heading);

  const attention = nextAttentionFromScreen(state);
  const decision = document.createElement("div");
  decision.className = attention.mode === "idle" ? "resumo-decision quiet" : "resumo-decision";

  const situacao = document.createElement("div");
  situacao.className = `decision-row signal-${attention.situationSignal}`;
  const situacaoLabel = document.createElement("span");
  situacaoLabel.className = "decision-label";
  situacaoLabel.textContent = "Situação";
  const situacaoBody = document.createElement("div");
  situacaoBody.className = "decision-situation";
  const signal = document.createElement("span");
  signal.className = `situation-signal signal-${attention.situationSignal}`;
  signal.setAttribute("aria-hidden", "true");
  const phrase = document.createElement("span");
  phrase.className = "decision-phrase";
  phrase.textContent = attention.situationPhrase;
  situacaoBody.append(signal, phrase);
  situacao.append(situacaoLabel, situacaoBody);

  const contexto = document.createElement("div");
  contexto.className = "decision-row";
  const contextoLabel = document.createElement("span");
  contextoLabel.className = "decision-label";
  contextoLabel.textContent = "Contexto disponível";
  const contextoValue = document.createElement("p");
  contextoValue.className = "decision-context";
  contextoValue.textContent = attention.context;
  contexto.append(contextoLabel, contextoValue);

  decision.append(situacao, contexto);

  const actions = primaryResumoActions(state, attention);
  const acao = document.createElement("div");
  acao.className = "decision-row decision-action";
  const acaoLabel = document.createElement("span");
  acaoLabel.className = "decision-label";
  acaoLabel.textContent = "Ação";
  acao.append(acaoLabel);
  if (actions.length > 0) {
    const nextWrap = document.createElement("div");
    nextWrap.className = attention.mode === "idle" ? "resumo-next optional" : "resumo-next";
    nextWrap.append(...actions);
    acao.append(nextWrap);
  } else {
    const none = document.createElement("p");
    none.className = "decision-context quiet";
    none.textContent =
      attention.kind === "choose-website" || attention.kind === "choose-project"
        ? "Escolher abaixo em Detalhe"
        : "Nenhum hop disponível";
    acao.append(none);
  }
  decision.append(acao);
  section.append(decision);
  return section;
};

/** 3. Quiet inventory — demoted below the cockpit. */
const renderResumoHoldings = (state: ClientScreen): HTMLElement => {
  const section = document.createElement("section");
  section.className = "resumo-section resumo-holdings resumo-inventory";
  const heading = document.createElement("h3");
  heading.className = "resumo-section-label";
  heading.textContent = "Detalhe";

  const list = document.createElement("dl");
  list.className = "resumo-lines holdings-lines";
  for (const line of composeClientHoldings(holdingsInputFromScreen(state))) {
    const row = document.createElement("div");
    row.className = "resumo-line";
    const dt = document.createElement("dt");
    dt.textContent = line.label;
    const dd = document.createElement("dd");
    dd.textContent = line.value;
    row.append(dt, dd);
    list.append(row);
  }
  section.append(heading, list);

  const extras = renderHoldingsExtras(state);
  if (extras !== null) {
    section.append(extras);
  }
  return section;
};

/** Chooser / multiples only — primary holdings already answered above. */
const renderHoldingsExtras = (state: ClientScreen): HTMLElement | null => {
  const parts: HTMLElement[] = [];
  const showWebsites = state.primary.kind === "chooser" || state.websites.length > 1;
  const showProjects =
    state.active?.kind === "chooser" ||
    state.active?.kind === "unavailable" ||
    state.projects.length > 1;

  if (showWebsites && !state.websiteReadDenied) {
    parts.push(renderResumoWebsites(state));
  }
  if (showProjects && !(state.projectReadDenied && state.projects.length === 0)) {
    parts.push(renderResumoProjects(state));
  }
  if (parts.length === 0) {
    return null;
  }
  const wrap = document.createElement("div");
  wrap.className = "resumo-quiet";
  wrap.append(...parts);
  return wrap;
};

/** 4. Histórico — quiet, important real events only. */
const renderResumoHistory = (state: ClientScreen): HTMLElement => {
  const section = document.createElement("section");
  section.className = "resumo-section resumo-history resumo-inventory";
  const heading = document.createElement("h3");
  heading.className = "resumo-section-label";
  heading.textContent = "Histórico";
  const list = document.createElement("ol");
  list.className = "resumo-history-list";
  for (const event of composeClientHistory(historyInputFromScreen(state))) {
    const item = document.createElement("li");
    item.className = "resumo-history-item";
    const text = document.createElement("span");
    text.className = "resumo-history-text";
    text.textContent = event.text;
    const when = document.createElement("span");
    when.className = "resumo-history-when";
    when.textContent = event.whenLabel;
    item.append(text, when);
    list.append(item);
  }
  section.append(heading, list);
  return section;
};

const resumoBeat = (label: string, ...body: readonly HTMLElement[]): HTMLElement => {
  const beat = document.createElement("div");
  beat.className = "resumo-beat";
  const heading = document.createElement("h3");
  heading.className = "resumo-beat-label";
  heading.textContent = label;
  const content = document.createElement("div");
  content.className = "resumo-beat-body";
  content.append(...body);
  beat.append(heading, content);
  return beat;
};

const sectionCard = (icon: string, title: string, body?: HTMLElement): HTMLElement => {
  const card = document.createElement("section");
  card.className = "card stack";
  const head = document.createElement("div");
  head.className = "card-head";
  const iconWrap = document.createElement("div");
  iconWrap.className = "card-icon";
  iconWrap.innerHTML = `<span class="icon">${icon}</span>`;
  const kicker = document.createElement("h3");
  kicker.textContent = title;
  head.append(iconWrap, kicker);
  card.append(head);
  if (body !== undefined) {
    card.append(body);
  }
  return card;
};

const mutedNote = (text: string): HTMLElement => {
  const note = document.createElement("p");
  note.className = "muted";
  note.textContent = text;
  return note;
};

const emptyState = (
  icon: string,
  title: string,
  body: string,
  action?: { readonly label: string; readonly onClick: () => void },
): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "empty-state";
  const iconWrap = document.createElement("div");
  iconWrap.className = "empty-icon";
  iconWrap.innerHTML = `<span class="icon lg">${icon}</span>`;
  const heading = document.createElement("h3");
  heading.textContent = title;
  const text = document.createElement("p");
  text.className = "muted";
  text.textContent = body;
  wrap.append(iconWrap, heading, text);
  if (action !== undefined) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = action.label;
    button.disabled = busy;
    button.addEventListener("click", action.onClick);
    wrap.append(button);
  }
  return wrap;
};

const renderResumoWebsites = (state: ClientScreen): HTMLElement => {
  const count = state.websites.length;
  const label = count === 1 ? "Website" : "Websites";

  if (state.websites.length === 0) {
    const empty = document.createElement("p");
    empty.className = "resumo-empty";
    empty.textContent = "Ainda sem websites neste workspace.";
    return resumoBeat(label, empty);
  }

  const primaryId = state.primary.kind === "known" ? state.primary.website.id : null;
  const ordered =
    primaryId === null
      ? state.websites
      : [
          ...state.websites.filter((item) => item.id === primaryId),
          ...state.websites.filter((item) => item.id !== primaryId),
        ];

  const list = document.createElement("div");
  list.className = "asset-list";
  for (const website of ordered) {
    list.append(renderWebsiteAsset(state, website));
  }
  return resumoBeat(label, list);
};

const renderWebsiteAsset = (state: ClientScreen, website: WebsiteSummary): HTMLElement => {
  const asset = document.createElement("article");
  const isPrimary = state.primary.kind === "known" && state.primary.website.id === website.id;
  asset.className = isPrimary ? "asset-card primary" : "asset-card";

  const head = document.createElement("div");
  head.className = "asset-head";
  const title = document.createElement("button");
  title.type = "button";
  title.className = "asset-title";
  title.disabled = busy;
  title.textContent = website.domain;
  title.addEventListener("click", () => {
    void withBusy(async () => {
      stashClientScreen(state);
      const reuse =
        state.primary.kind === "known" && state.primary.website.id === website.id
          ? {
              website: websiteDetailFromSummary(website),
              active: state.active,
              projectDetail: state.projectDetail,
            }
          : undefined;
      await openWebsiteScreen(state.client, website.id, state.members, reuse);
    });
  });
  const badges = document.createElement("div");
  badges.className = "chip-row";
  if (isPrimary && state.websites.length > 1) {
    badges.append(statusBadge("Principal", true));
  }
  if (badges.childElementCount > 0) {
    head.append(title, badges);
  } else {
    head.append(title);
  }

  if (state.primary.kind === "chooser") {
    const pick = document.createElement("button");
    pick.type = "button";
    pick.className = "ghost asset-pick";
    pick.disabled = busy;
    pick.textContent = "Definir principal";
    pick.addEventListener("click", () => {
      void withBusy(async () => {
        if (screen.name !== "client") {
          return;
        }
        const loaded = await resolveActiveFromLoaded(
          website.projectId,
          state.projects,
          state.projectReadDenied && state.projects.length === 0,
        );
        const next: ClientScreen = {
          ...screen,
          primary: { kind: "known", website },
          active: loaded.active,
          projectDetail: loaded.projectDetail,
          projectReadDenied: loaded.projectReadDenied,
          projectChooserPick: null,
        };
        clientScreenStash = next;
        screen = next;
      });
    });
    head.append(pick);
  }

  asset.append(head);

  const facts = document.createElement("p");
  facts.className = "asset-meta";
  const bits: string[] = [
    formatWebsiteEnvironment(website.environment),
    formatWebsiteStatus(website.status),
  ];
  if (state.primary.kind === "chooser") {
    bits.push(formatRelativeActivity(website.updatedAt));
  }
  facts.textContent = bits.join(" · ");
  asset.append(facts);
  return asset;
};

const resolvedProjectId = (state: ClientScreen): string | null => {
  if (state.active === null) {
    return state.projectDetail?.id ?? null;
  }
  if (state.active.kind === "linked" && state.projectDetail !== null) {
    return state.active.projectId;
  }
  if (state.active.kind === "selected") {
    return state.active.project.id;
  }
  if (state.active.kind === "chooser" && state.projectChooserPick !== null) {
    return state.projectChooserPick;
  }
  return state.projectDetail?.id ?? null;
};

const renderResumoProjects = (state: ClientScreen): HTMLElement => {
  const activeId = resolvedProjectId(state);
  const count = state.projects.length;
  const label = count === 1 ? "Projeto" : "Projetos";
  const body: HTMLElement[] = [];

  if (state.active?.kind === "unavailable") {
    const unavailable = document.createElement("p");
    unavailable.className = "resumo-empty";
    unavailable.textContent = "Projeto ligado indisponível.";
    body.push(unavailable);
  }

  if (state.active?.kind === "chooser") {
    const chooser = document.createElement("ul");
    chooser.className = "list";
    for (const candidate of state.active.candidates) {
      const li = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "item";
      button.disabled = busy;
      const deadline =
        candidate.plannedCompletion !== null
          ? ` · prazo ${formatDate(candidate.plannedCompletion)}`
          : "";
      const badgeClass = isPositiveProjectStatus(candidate.status) ? "badge" : "badge inactive";
      button.innerHTML = `<strong>${escapeHtml(candidate.name)}</strong><span class="muted"><span class="${badgeClass}">${escapeHtml(formatProjectStatus(candidate.status))}</span>${escapeHtml(deadline)}</span>`;
      button.addEventListener("click", () => {
        void withBusy(async () => {
          const detail = await getProject(candidate.id);
          if (screen.name !== "client") {
            return;
          }
          const next: ClientScreen = {
            ...screen,
            projectDetail: detail,
            projectChooserPick: candidate.id,
            active: { kind: "selected", project: candidate },
          };
          clientScreenStash = next;
          screen = next;
        });
      });
      li.append(button);
      chooser.append(li);
    }
    body.push(chooser);
  }

  if (state.projects.length === 0) {
    const empty = document.createElement("p");
    empty.className = "resumo-empty";
    empty.textContent = "Ainda sem projetos neste workspace.";
    return resumoBeat(label, ...body, empty);
  }

  const ordered =
    activeId === null
      ? state.projects
      : [
          ...state.projects.filter((item) => item.id === activeId),
          ...state.projects.filter((item) => item.id !== activeId),
        ];

  const list = document.createElement("div");
  list.className = "asset-list";
  for (const project of ordered) {
    list.append(renderProjectAsset(state, project, project.id === activeId));
  }
  return resumoBeat(label, ...body, list);
};

const renderProjectAsset = (
  state: ClientScreen,
  project: ProjectSummary,
  isActive: boolean,
): HTMLElement => {
  const asset = document.createElement("article");
  asset.className = isActive ? "asset-card project-asset active" : "asset-card project-asset";

  const head = document.createElement("div");
  head.className = "asset-head";
  const title = document.createElement("button");
  title.type = "button";
  title.className = "asset-title";
  title.disabled = busy;
  title.textContent = project.name;
  title.addEventListener("click", () => {
    void withBusy(async () => {
      stashClientScreen(state);
      const detail =
        state.projectDetail !== null && state.projectDetail.id === project.id
          ? state.projectDetail
          : await getProject(project.id);
      let linked: WebsiteSummary | undefined;
      if (state.primary.kind === "known" && state.primary.website.projectId === project.id) {
        const primaryId = state.primary.website.id;
        linked = state.websites.find((website) => website.id === primaryId);
      }
      await enterProjectScreen({
        client: state.client,
        project: detail,
        members: state.members,
        ...(linked !== undefined ? { linkedWebsite: linked } : {}),
      });
    });
  });
  if (isActive && state.projects.length > 1) {
    const badges = document.createElement("div");
    badges.className = "chip-row";
    badges.append(statusBadge("Ativo", true));
    head.append(title, badges);
  } else {
    head.append(title);
  }

  asset.append(head);

  const facts = document.createElement("p");
  facts.className = "asset-meta";
  const bits: string[] = [formatProjectStatus(project.status)];
  if (project.plannedCompletion !== null) {
    bits.push(`prazo ${formatDate(project.plannedCompletion)}`);
  }
  facts.textContent = bits.join(" · ");
  asset.append(facts);
  return asset;
};

const primaryResumoActions = (
  state: ClientScreen,
  attention: NextAttention,
): readonly HTMLElement[] => {
  const actions: HTMLElement[] = [];

  const openWebsiteButton = (): HTMLButtonElement | null => {
    if (state.primary.kind !== "known") {
      return null;
    }
    const openWebsite = document.createElement("button");
    openWebsite.type = "button";
    openWebsite.textContent = "Abrir Website →";
    openWebsite.disabled = busy;
    openWebsite.addEventListener("click", () => {
      void withBusy(
        async () => {
          if (state.primary.kind !== "known") {
            return;
          }
          const primaryWebsite = state.primary.website;
          stashClientScreen(state);
          const summary = state.websites.find((item) => item.id === primaryWebsite.id);
          const reuse: WebsiteReuse =
            summary !== undefined
              ? {
                  website: websiteDetailFromSummary(summary),
                  active: state.active,
                  projectDetail: state.projectDetail,
                }
              : {
                  active: state.active,
                  projectDetail: state.projectDetail,
                };
          await openWebsiteScreen(state.client, primaryWebsite.id, state.members, reuse);
        },
        { freezeShell: false },
      );
    });
    return openWebsite;
  };

  const openProjectButton = (): HTMLButtonElement | null => {
    const projectId = resolvedProjectId(state);
    if (projectId === null || state.active?.kind === "chooser") {
      return null;
    }
    const openProject = document.createElement("button");
    openProject.type = "button";
    openProject.textContent = "Abrir Projeto →";
    openProject.disabled = busy;
    openProject.addEventListener("click", () => {
      void withBusy(
        async () => {
          stashClientScreen(state);
          const detail =
            state.projectDetail !== null && state.projectDetail.id === projectId
              ? state.projectDetail
              : await getProject(projectId);
          let linked: WebsiteSummary | undefined;
          if (state.primary.kind === "known" && state.primary.website.projectId === projectId) {
            const primaryId = state.primary.website.id;
            linked = state.websites.find((website) => website.id === primaryId);
          }
          await enterProjectScreen({
            client: state.client,
            project: detail,
            members: state.members,
            ...(linked !== undefined ? { linkedWebsite: linked } : {}),
          });
        },
        { freezeShell: false },
      );
    });
    return openProject;
  };

  const website = openWebsiteButton();
  const project = openProjectButton();

  if (attention.kind === "open-website" && website !== null) {
    actions.push(website);
    return actions;
  }

  if (attention.kind === "open-project" && project !== null) {
    actions.push(project);
    return actions;
  }

  return actions;
};

const renderClientWebsitesTab = (state: ClientScreen): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack";

  if (state.websiteReadDenied) {
    wrap.append(mutedNote("Sem permissão para ver websites deste cliente."));
    return wrap;
  }

  if (state.websites.length === 0) {
    wrap.append(
      emptyState(
        iconGlobe(),
        "Sem websites visíveis",
        "Este cliente ainda não tem websites neste workspace. Regista o primeiro website para o acompanhar no Resumo.",
        {
          label: "Novo website",
          onClick: () => {
            void withBusy(async () => {
              await openCreateWebsite(state.client.id);
            });
          },
        },
      ),
    );
    return wrap;
  }

  const createRow = document.createElement("div");
  createRow.className = "row";
  const createButton = document.createElement("button");
  createButton.type = "button";
  createButton.textContent = "Novo website";
  createButton.disabled = busy;
  createButton.addEventListener("click", () => {
    void withBusy(async () => {
      await openCreateWebsite(state.client.id);
    });
  });
  createRow.append(createButton);
  wrap.append(createRow);

  const list = document.createElement("ul");
  list.className = "list";
  for (const website of state.websites) {
    const li = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "item";
    button.disabled = busy;
    const badgeClass = isPositiveWebsiteStatus(website.status) ? "badge" : "badge inactive";
    const tech = website.technology !== null ? ` · ${escapeHtml(website.technology)}` : "";
    button.innerHTML = `<strong>${escapeHtml(website.domain)}</strong><span class="muted"><span class="${badgeClass}">${escapeHtml(formatWebsiteStatus(website.status))}</span> · ${escapeHtml(formatWebsiteEnvironment(website.environment))}${tech} · resp. ${escapeHtml(responsibleLabel(website.ownerMembershipId, state.members))}</span>`;
    button.addEventListener("click", () => {
      void withBusy(async () => {
        stashClientScreen(state);
        const reuse =
          state.primary.kind === "known" && state.primary.website.id === website.id
            ? {
                website: websiteDetailFromSummary(website),
                active: state.active,
                projectDetail: state.projectDetail,
              }
            : { website: websiteDetailFromSummary(website) };
        await openWebsiteScreen(state.client, website.id, state.members, reuse);
      });
    });
    li.append(button);
    list.append(li);
  }
  wrap.append(list);
  return wrap;
};

const openWebsiteScreen = async (
  client: ClientDetail,
  websiteId: string,
  members: ReadonlyMap<string, string>,
  reuse?: WebsiteReuse,
): Promise<void> => {
  const website = reuse?.website?.id === websiteId ? reuse.website : await getWebsite(websiteId);

  let active: ActiveProjectResolution;
  let projectDetail: ProjectDetail | null = null;

  const reusedActive = reuse?.active;
  if (
    reusedActive !== undefined &&
    reusedActive !== null &&
    (reuse?.website === undefined || reuse.website.id === websiteId)
  ) {
    active = reusedActive;
    projectDetail = reuse?.projectDetail ?? null;
  } else if (website.projectId !== null) {
    try {
      projectDetail = await getProject(website.projectId);
      active = { kind: "linked", projectId: website.projectId };
    } catch {
      active = activeProjectUnavailable(website.projectId);
    }
  } else {
    try {
      const projects = await listAllProjectsForClient(client.id);
      active = resolveActiveProject(null, projects);
      if (active.kind === "selected") {
        projectDetail = await getProject(active.project.id);
      }
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 403) {
        active = { kind: "empty" };
      } else {
        throw error;
      }
    }
  }

  screen = {
    name: "website",
    client,
    website,
    active,
    projectDetail,
    members,
    chooserPick: null,
  };
};

const resolvedWebsiteProjectId = (
  active: ActiveProjectResolution,
  projectDetail: ProjectDetail | null,
  chooserPick: string | null,
): string | null => {
  if (active.kind === "linked" && projectDetail !== null) {
    return active.projectId;
  }
  if (active.kind === "selected") {
    return active.project.id;
  }
  if (active.kind === "chooser" && chooserPick !== null) {
    return chooserPick;
  }
  return null;
};

const backToClient = (client: ClientDetail): void => {
  void withBusy(
    async () => {
      if (restoreClientScreen(client.id)) {
        return;
      }
      await openClient(client.id, client.name);
    },
    { freezeShell: false, skipInitialRender: true },
  );
};

const renderClientCrumb = (client: ClientDetail): HTMLElement => {
  const row = document.createElement("div");
  row.className = "breadcrumb";
  const back = document.createElement("button");
  back.type = "button";
  back.textContent = client.name;
  back.disabled = busy;
  back.addEventListener("click", () => {
    backToClient(client);
  });
  row.append(back);
  return row;
};

const renderWebsite = (
  client: ClientDetail,
  website: WebsiteDetail,
  active: ActiveProjectResolution,
  projectDetail: ProjectDetail | null,
  members: ReadonlyMap<string, string>,
  chooserPick: string | null,
): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack website-view";
  const crumb = renderClientCrumb(client);
  const sep = document.createElement("span");
  sep.textContent = "·";
  const current = document.createElement("span");
  current.textContent = website.domain;
  crumb.append(sep, current);
  wrap.append(crumb);

  const header = document.createElement("div");
  header.className = "identity website-hero";
  const avatar = document.createElement("div");
  avatar.className = "avatar";
  avatar.setAttribute("aria-hidden", "true");
  avatar.textContent = initialsFromName(website.domain);
  const main = document.createElement("div");
  main.className = "identity-main";
  const titleRow = document.createElement("div");
  titleRow.className = "identity-title";
  const heading = document.createElement("h2");
  heading.textContent = website.domain;
  titleRow.append(heading);
  const meta = document.createElement("p");
  meta.className = "identity-meta";
  meta.textContent = `Cliente ${client.name}`;
  main.append(titleRow, meta);
  header.append(avatar, main);
  wrap.append(header);
  if (website.archivedAt === null) {
    wrap.append(
      renderLifecycleActions({
        onEdit: () => {
          screen = { name: "edit-website", client, website };
          render();
        },
        onArchive: () => {
          void withBusy(async () => {
            if (
              !window.confirm(
                `Arquivar o website «${website.domain}»? Sai da lista ativa; o cliente e projetos ligados não são alterados automaticamente.`,
              )
            ) {
              return;
            }
            await archiveWebsite({
              websiteId: website.id,
              expectedVersion: website.version,
            });
            catalog = null;
            clientScreenStash = null;
            lastSearchScreen = initialSearchScreen();
            await openClient(client.id, client.name);
          });
        },
      }),
    );
  }

  const cockpit = renderWebsiteCockpit(
    client,
    website,
    active,
    projectDetail,
    members,
    chooserPick,
  );
  wrap.append(cockpit);
  return wrap;
};

const websiteWorkspaceFromScreen = (
  website: WebsiteDetail,
  active: ActiveProjectResolution,
  projectDetail: ProjectDetail | null,
  members: ReadonlyMap<string, string>,
  chooserPick: string | null,
): WebsiteWorkspaceView => {
  const projectId = resolvedWebsiteProjectId(active, projectDetail, chooserPick);
  const project =
    projectDetail !== null && projectId !== null && projectDetail.id === projectId
      ? projectDetail
      : active.kind === "selected"
        ? active.project
        : null;
  const projectName = project?.name ?? null;
  const projectStatus = project?.status ?? null;
  const latestProjectAt =
    projectDetail !== null
      ? projectDetail.updatedAt
      : active.kind === "selected"
        ? active.project.updatedAt
        : null;

  return composeWebsiteWorkspace({
    domain: website.domain,
    environment: website.environment,
    status: website.status,
    technology: website.technology,
    responsible: responsibleName(website.ownerMembershipId, members),
    updatedRelative: formatRelativeActivity(website.updatedAt),
    projectName,
    projectStatus,
    projectNeedsChoice: active.kind === "chooser" && chooserPick === null,
    projectUnavailable: active.kind === "unavailable",
    hasOpenableProject: projectId !== null && active.kind !== "chooser",
    websiteCreatedAt: website.createdAt,
    websiteCreatedLabel: formatChangeWhen(website.createdAt),
    projectUpdatedAt: latestProjectAt,
    projectUpdatedLabel: latestProjectAt !== null ? formatChangeWhen(latestProjectAt) : null,
  });
};

/** Website Workspace cockpit — Situação → Contexto → Projeto → Atividade → Ação. */
const renderWebsiteCockpit = (
  client: ClientDetail,
  website: WebsiteDetail,
  active: ActiveProjectResolution,
  projectDetail: ProjectDetail | null,
  members: ReadonlyMap<string, string>,
  chooserPick: string | null,
): HTMLElement => {
  const view = websiteWorkspaceFromScreen(website, active, projectDetail, members, chooserPick);
  const section = document.createElement("section");
  section.className = "resumo-section website-cockpit-block";

  const decision = document.createElement("div");
  decision.className =
    view.situationSignal === "ok" && view.actionKind !== "open-project"
      ? "resumo-decision quiet"
      : "resumo-decision";

  const situacao = document.createElement("div");
  situacao.className = `decision-row signal-${view.situationSignal}`;
  const situacaoLabel = document.createElement("span");
  situacaoLabel.className = "decision-label";
  situacaoLabel.textContent = "Situação";
  const situacaoBody = document.createElement("div");
  situacaoBody.className = "decision-situation";
  const signal = document.createElement("span");
  signal.className = `situation-signal signal-${view.situationSignal}`;
  signal.setAttribute("aria-hidden", "true");
  const phrase = document.createElement("span");
  phrase.className = "decision-phrase";
  phrase.textContent = view.situationPhrase;
  situacaoBody.append(signal, phrase);
  situacao.append(situacaoLabel, situacaoBody);

  const contexto = document.createElement("div");
  contexto.className = "decision-row";
  const contextoLabel = document.createElement("span");
  contextoLabel.className = "decision-label";
  contextoLabel.textContent = "Contexto";
  const contextoValue = document.createElement("p");
  contextoValue.className = "decision-context";
  contextoValue.textContent = view.context;
  contexto.append(contextoLabel, contextoValue);

  const projeto = document.createElement("div");
  projeto.className = `decision-row signal-${view.project.signal}`;
  const projetoLabel = document.createElement("span");
  projetoLabel.className = "decision-label";
  projetoLabel.textContent = "Projeto";
  const projetoBody = document.createElement("div");
  projetoBody.className = "decision-situation";
  const projetoSignal = document.createElement("span");
  projetoSignal.className = `situation-signal signal-${view.project.signal}`;
  projetoSignal.setAttribute("aria-hidden", "true");
  const projetoPhrase = document.createElement("span");
  projetoPhrase.className = "decision-phrase";
  projetoPhrase.textContent = view.project.phrase;
  projetoBody.append(projetoSignal, projetoPhrase);
  projeto.append(projetoLabel, projetoBody);

  decision.append(situacao, contexto, projeto);

  if (active.kind === "chooser" && chooserPick === null) {
    const chooser = document.createElement("div");
    chooser.className = "decision-row";
    chooser.append(renderActiveProject(active, projectDetail, members, chooserPick));
    decision.append(chooser);
  }

  const atividade = document.createElement("div");
  atividade.className = "decision-row";
  const atividadeLabel = document.createElement("span");
  atividadeLabel.className = "decision-label";
  atividadeLabel.textContent = "Atividade";
  const list = document.createElement("ol");
  list.className = "resumo-history-list website-activity-list";
  for (const event of view.activity) {
    const item = document.createElement("li");
    item.className = "resumo-history-item";
    const text = document.createElement("span");
    text.className = "resumo-history-text";
    text.textContent = event.text;
    const when = document.createElement("span");
    when.className = "resumo-history-when";
    when.textContent = event.whenLabel;
    item.append(text, when);
    list.append(item);
  }
  atividade.append(atividadeLabel, list);

  const acao = document.createElement("div");
  acao.className = "decision-row decision-action";
  const acaoLabel = document.createElement("span");
  acaoLabel.className = "decision-label";
  acaoLabel.textContent = "Ação";
  const nextWrap = document.createElement("div");
  nextWrap.className =
    view.actionKind === "back-to-client" ? "resumo-next optional" : "resumo-next";
  nextWrap.append(
    renderWebsitePrimaryAction(client, website, active, projectDetail, members, chooserPick, view),
  );
  acao.append(acaoLabel, nextWrap);

  decision.append(atividade, acao);
  section.append(decision);
  return section;
};

const renderWebsitePrimaryAction = (
  client: ClientDetail,
  website: WebsiteDetail,
  active: ActiveProjectResolution,
  projectDetail: ProjectDetail | null,
  members: ReadonlyMap<string, string>,
  chooserPick: string | null,
  view: WebsiteWorkspaceView,
): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "row";

  const button = document.createElement("button");
  button.type = "button";
  button.textContent = `${view.actionLabel} →`;
  button.disabled = busy || view.actionKind === "choose-project";

  if (view.actionKind === "open-project") {
    const projectId = resolvedWebsiteProjectId(active, projectDetail, chooserPick);
    if (projectId !== null) {
      button.addEventListener("click", () => {
        void withBusy(async () => {
          const detail =
            projectDetail !== null && projectDetail.id === projectId
              ? projectDetail
              : await getProject(projectId);
          await enterProjectScreen({
            client,
            project: detail,
            members,
            linkedWebsite: website,
          });
        });
      });
    }
    wrap.append(button);
    return wrap;
  }

  if (view.actionKind === "back-to-client") {
    button.className = "secondary";
    button.addEventListener("click", () => {
      backToClient(client);
    });
  } else {
    button.className = "secondary";
  }
  wrap.append(button);

  const createProjectButton = document.createElement("button");
  createProjectButton.type = "button";
  createProjectButton.textContent = "Novo projeto";
  createProjectButton.disabled = busy;
  createProjectButton.addEventListener("click", () => {
    void withBusy(async () => {
      await openCreateProject(client.id, website.id);
    });
  });
  wrap.append(createProjectButton);

  return wrap;
};

const renderProjectScreen = (
  client: ClientDetail,
  project: ProjectDetail,
  members: ReadonlyMap<string, string>,
  linkedWebsite: WebsiteSummary | null,
): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "stack project-view";
  const crumb = renderClientCrumb(client);
  const sep = document.createElement("span");
  sep.textContent = "·";
  const current = document.createElement("span");
  current.textContent = project.name;
  crumb.append(sep, current);
  wrap.append(crumb);

  const header = document.createElement("div");
  header.className = "identity project-hero";
  const avatar = document.createElement("div");
  avatar.className = "avatar";
  avatar.setAttribute("aria-hidden", "true");
  avatar.textContent = initialsFromName(project.name);
  const main = document.createElement("div");
  main.className = "identity-main";
  const titleRow = document.createElement("div");
  titleRow.className = "identity-title";
  const heading = document.createElement("h2");
  heading.textContent = project.name;
  titleRow.append(heading);
  const meta = document.createElement("p");
  meta.className = "identity-meta";
  meta.textContent = formatProjectStatus(project.status);
  main.append(titleRow, meta);
  header.append(avatar, main);
  wrap.append(header);
  if (project.archivedAt === null) {
    wrap.append(
      renderLifecycleActions({
        onEdit: () => {
          screen = {
            name: "edit-project",
            client,
            project,
            linkedWebsite,
          };
          render();
        },
        onArchive: () => {
          void withBusy(async () => {
            if (
              !window.confirm(
                `Arquivar o projeto «${project.name}»? Sai da lista ativa; o cliente e websites ligados não são alterados automaticamente.`,
              )
            ) {
              return;
            }
            await archiveProject({
              projectId: project.id,
              expectedVersion: project.version,
            });
            catalog = null;
            clientScreenStash = null;
            lastSearchScreen = initialSearchScreen();
            await openClient(client.id, client.name);
          });
        },
      }),
    );
  }

  wrap.append(renderProjectCockpit(client, project, members, linkedWebsite));
  return wrap;
};

const projectWorkspaceFromScreen = (
  project: ProjectDetail,
  members: ReadonlyMap<string, string>,
  linkedWebsite: WebsiteSummary | null,
): ProjectWorkspaceView =>
  composeProjectWorkspace({
    status: project.status,
    name: project.name,
    description: project.description,
    responsible: responsibleName(project.ownerMembershipId, members),
    updatedRelative: formatRelativeActivity(project.updatedAt),
    websiteDomain: linkedWebsite?.domain ?? null,
    websiteEnvironment: linkedWebsite?.environment ?? null,
    websiteStatus: linkedWebsite?.status ?? null,
    hasOpenableWebsite: linkedWebsite !== null,
    projectCreatedAt: project.createdAt,
    projectCreatedLabel: formatChangeWhen(project.createdAt),
    projectUpdatedAt: project.updatedAt,
    projectUpdatedLabel: formatChangeWhen(project.updatedAt),
    openAttentions: project.openAttentions,
  });

/** Project Workspace cockpit — Situação → Atenção? → Contexto → Atividade → Ação (+ write). */
const renderProjectCockpit = (
  client: ClientDetail,
  project: ProjectDetail,
  members: ReadonlyMap<string, string>,
  linkedWebsite: WebsiteSummary | null,
): HTMLElement => {
  const view = projectWorkspaceFromScreen(project, members, linkedWebsite);
  const openAttentionDetail =
    project.openAttentions.find((attention) => attention.status === "OPEN") ?? null;
  const section = document.createElement("section");
  section.className = "resumo-section project-cockpit-block";

  const decision = document.createElement("div");
  decision.className =
    view.attention !== null || view.actionKind === "view-attention"
      ? "resumo-decision"
      : view.situationSignal === "ok" && view.actionKind !== "open-website"
        ? "resumo-decision quiet"
        : "resumo-decision";

  const situacao = document.createElement("div");
  situacao.className = `decision-row signal-${view.situationSignal}`;
  const situacaoLabel = document.createElement("span");
  situacaoLabel.className = "decision-label";
  situacaoLabel.textContent = "Situação";
  const situacaoBody = document.createElement("div");
  situacaoBody.className = "decision-situation";
  const signal = document.createElement("span");
  signal.className = `situation-signal signal-${view.situationSignal}`;
  signal.setAttribute("aria-hidden", "true");
  const phrase = document.createElement("span");
  phrase.className = "decision-phrase";
  phrase.textContent = view.situationPhrase;
  situacaoBody.append(signal, phrase);
  situacao.append(situacaoLabel, situacaoBody);

  decision.append(situacao);

  let attentionDetail: HTMLElement | null = null;
  if (view.attention !== null && openAttentionDetail !== null) {
    const atencao = document.createElement("div");
    atencao.className = `decision-row signal-${view.attention.signal}`;
    const atencaoLabel = document.createElement("span");
    atencaoLabel.className = "decision-label";
    atencaoLabel.textContent = "Atenção";
    const atencaoBody = document.createElement("div");
    atencaoBody.className = "decision-attention-body";
    const atencaoTitle = document.createElement("p");
    atencaoTitle.className = "resumo-attention";
    const atencaoSignal = document.createElement("span");
    atencaoSignal.className = `situation-signal signal-${view.attention.signal}`;
    atencaoSignal.setAttribute("aria-hidden", "true");
    const atencaoPhrase = document.createElement("span");
    atencaoPhrase.textContent = view.attention.title;
    atencaoTitle.append(atencaoSignal, atencaoPhrase);
    const atencaoOrigin = document.createElement("p");
    atencaoOrigin.className = "decision-context";
    atencaoOrigin.textContent = view.attention.origin;
    const atencaoQuote = document.createElement("blockquote");
    atencaoQuote.className = "project-attention-quote";
    atencaoQuote.textContent = `"${view.attention.quote}"`;
    atencaoBody.append(atencaoTitle, atencaoOrigin, atencaoQuote);
    atencao.append(atencaoLabel, atencaoBody);
    decision.append(atencao);

    attentionDetail = document.createElement("div");
    attentionDetail.className = "project-attention-detail hidden";
    attentionDetail.id = "project-attention-detail";
    const detailText = document.createElement("p");
    detailText.className = "decision-context";
    detailText.textContent = view.attention.detail;
    const suggested = document.createElement("p");
    suggested.className = "decision-context";
    suggested.textContent = `Ação sugerida: ${view.attention.suggestedAction}`;
    attentionDetail.append(detailText, suggested);
    decision.append(attentionDetail);

    const resolveRow = document.createElement("div");
    resolveRow.className = "decision-row project-attention-resolve-row";
    const resolveLabel = document.createElement("span");
    resolveLabel.className = "decision-label";
    resolveLabel.textContent = "Resolver";
    const resolveWrap = document.createElement("div");
    resolveWrap.className = "resumo-next";
    const resolveButton = document.createElement("button");
    resolveButton.type = "button";
    resolveButton.className = "secondary";
    resolveButton.textContent = busy ? "A resolver…" : "Resolver atenção";
    resolveButton.disabled = busy;
    resolveButton.dataset.attentionId = openAttentionDetail.id;
    resolveButton.addEventListener("click", () => {
      void withBusy(async () => {
        await resolveAttention({
          projectId: project.id,
          attentionId: openAttentionDetail.id,
        });
        await openProjectByIds(client.id, project.id);
      });
    });
    resolveWrap.append(resolveButton);
    resolveRow.append(resolveLabel, resolveWrap);
    decision.append(resolveRow);
  }

  const contexto = document.createElement("div");
  contexto.className = "decision-row";
  const contextoLabel = document.createElement("span");
  contextoLabel.className = "decision-label";
  contextoLabel.textContent = "Contexto";
  const contextoList = document.createElement("dl");
  contextoList.className = "resumo-lines project-context-lines";
  for (const field of view.contextFields) {
    const row = document.createElement("div");
    row.className = "resumo-line";
    const dt = document.createElement("dt");
    dt.textContent = field.label;
    const dd = document.createElement("dd");
    dd.textContent = field.value;
    row.append(dt, dd);
    contextoList.append(row);
  }
  contexto.append(contextoLabel, contextoList);

  decision.append(contexto);

  if (view.website !== null) {
    const website = document.createElement("div");
    website.className = `decision-row signal-${view.website.signal}`;
    const websiteLabel = document.createElement("span");
    websiteLabel.className = "decision-label";
    websiteLabel.textContent = "Website";
    const websiteBody = document.createElement("div");
    websiteBody.className = "decision-situation";
    const websiteSignal = document.createElement("span");
    websiteSignal.className = `situation-signal signal-${view.website.signal}`;
    websiteSignal.setAttribute("aria-hidden", "true");
    const websitePhrase = document.createElement("span");
    websitePhrase.className = "decision-phrase";
    websitePhrase.textContent = view.website.phrase;
    websiteBody.append(websiteSignal, websitePhrase);
    website.append(websiteLabel, websiteBody);
    decision.append(website);
  }

  const atividade = document.createElement("div");
  atividade.className = "decision-row";
  const atividadeLabel = document.createElement("span");
  atividadeLabel.className = "decision-label";
  atividadeLabel.textContent = "Atividade";
  const list = document.createElement("ol");
  list.className = "resumo-history-list project-activity-list";
  for (const event of view.activity) {
    const item = document.createElement("li");
    item.className = "resumo-history-item";
    const text = document.createElement("span");
    text.className = "resumo-history-text";
    text.textContent = event.text;
    const when = document.createElement("span");
    when.className = "resumo-history-when";
    when.textContent = event.whenLabel;
    item.append(text, when);
    list.append(item);
  }
  atividade.append(atividadeLabel, list);

  const acao = document.createElement("div");
  acao.className = "decision-row decision-action";
  const acaoLabel = document.createElement("span");
  acaoLabel.className = "decision-label";
  acaoLabel.textContent = "Ação";
  const nextWrap = document.createElement("div");
  nextWrap.className =
    view.actionKind === "back-to-client" ? "resumo-next optional" : "resumo-next";
  nextWrap.append(
    renderProjectPrimaryAction(client, members, linkedWebsite, view, attentionDetail),
  );
  acao.append(acaoLabel, nextWrap);

  decision.append(atividade, acao);
  section.append(decision);
  section.append(renderProjectAttentionCreateForm(client, project));
  return section;
};

const renderProjectAttentionCreateForm = (
  client: ClientDetail,
  project: ProjectDetail,
): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.className = "project-attention-write";
  wrap.dataset.projectId = project.id;

  const heading = document.createElement("h3");
  heading.className = "project-attention-write-title";
  heading.textContent = "Registar atenção";

  const intro = document.createElement("p");
  intro.className = "muted";
  intro.textContent =
    "Registo operacional no projeto atual. O UTURA OS não verifica automaticamente estes valores.";

  const form = document.createElement("form");
  form.className = "stack project-attention-create-form";
  form.setAttribute("data-attention-create", "true");

  const titleLabel = document.createElement("label");
  titleLabel.textContent = "Título";
  const titleInput = document.createElement("input");
  titleInput.name = "title";
  titleInput.type = "text";
  titleInput.required = true;
  titleInput.minLength = 2;
  titleInput.maxLength = 160;
  titleInput.placeholder = "Título da atenção";
  titleInput.autocomplete = "off";
  titleLabel.append(titleInput);

  const descriptionLabel = document.createElement("label");
  descriptionLabel.textContent = "Descrição";
  const descriptionInput = document.createElement("textarea");
  descriptionInput.name = "description";
  descriptionInput.required = true;
  descriptionInput.minLength = 1;
  descriptionInput.maxLength = 10_000;
  descriptionInput.rows = 3;
  descriptionInput.placeholder = "O que o cliente ou a agência registou";
  descriptionLabel.append(descriptionInput);

  const sourceLabel = document.createElement("label");
  sourceLabel.textContent = "Origem";
  const sourceSelect = document.createElement("select");
  sourceSelect.name = "source";
  sourceSelect.required = true;
  for (const source of ["CUSTOMER_REPORTED", "OPERATOR_RECORDED"] as const) {
    const option = document.createElement("option");
    option.value = source;
    option.textContent = formatAttentionSource(source);
    if (source === "CUSTOMER_REPORTED") {
      option.selected = true;
    }
    sourceSelect.append(option);
  }
  sourceLabel.append(sourceSelect);

  const actions = document.createElement("div");
  actions.className = "row";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.textContent = busy ? "A registar…" : "Registar atenção";
  submit.disabled = busy;
  actions.append(submit);

  form.append(titleLabel, descriptionLabel, sourceLabel, actions);
  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    const data = new FormData(form);
    const title = formString(data, "title").trim();
    const description = formString(data, "description").trim();
    const sourceRaw = formString(data, "source").trim();
    const source = sourceRaw === "OPERATOR_RECORDED" ? "OPERATOR_RECORDED" : "CUSTOMER_REPORTED";
    void withBusy(async () => {
      await createAttention({
        projectId: project.id,
        title,
        description,
        source,
      });
      await openProjectByIds(client.id, project.id);
    });
  });

  wrap.append(heading, intro, form);
  return wrap;
};

const renderProjectPrimaryAction = (
  client: ClientDetail,
  members: ReadonlyMap<string, string>,
  linkedWebsite: WebsiteSummary | null,
  view: ProjectWorkspaceView,
  attentionDetail: HTMLElement | null,
): HTMLElement => {
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = `${view.actionLabel} →`;
  button.disabled = busy;

  if (view.actionKind === "view-attention" && attentionDetail !== null) {
    button.addEventListener("click", () => {
      const hidden = attentionDetail.classList.contains("hidden");
      attentionDetail.classList.toggle("hidden", !hidden);
      button.textContent = hidden ? "Ocultar atenção →" : "Ver atenção →";
      if (hidden) {
        attentionDetail.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    });
    return button;
  }

  if (view.actionKind === "open-website" && linkedWebsite !== null) {
    button.addEventListener("click", () => {
      void withBusy(async () => {
        await openWebsiteScreen(client, linkedWebsite.id, members, {
          website: websiteDetailFromSummary(linkedWebsite),
        });
      });
    });
    return button;
  }

  button.className = "secondary";
  button.addEventListener("click", () => {
    backToClient(client);
  });
  return button;
};

const renderActiveProject = (
  active: ActiveProjectResolution,
  projectDetail: ProjectDetail | null,
  members: ReadonlyMap<string, string>,
  chooserPick: string | null,
): HTMLElement => {
  const box = document.createElement("div");
  box.className = "stack";

  if (active.kind === "empty") {
    box.append(
      emptyState(
        iconFolder(),
        "Sem projeto ativo",
        "Este website ainda não tem um projeto resolvido. Podes voltar ao cliente sem sair do workspace.",
      ),
    );
    return box;
  }

  if (active.kind === "unavailable") {
    const unavailable = document.createElement("p");
    unavailable.className = "error";
    unavailable.textContent = `Projeto ligado indisponível (${active.projectId}). Sem fallback silencioso.`;
    box.append(unavailable);
    return box;
  }

  if (active.kind === "chooser") {
    box.append(mutedNote("Vários projetos no mesmo nível — escolhe um (somente leitura)."));
    const list = document.createElement("ul");
    list.className = "list";
    for (const candidate of active.candidates) {
      const li = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "item";
      button.disabled = busy;
      const deadline =
        candidate.plannedCompletion !== null
          ? ` · prazo ${formatDate(candidate.plannedCompletion)}`
          : "";
      const badgeClass = isPositiveProjectStatus(candidate.status) ? "badge" : "badge inactive";
      button.innerHTML = `<strong>${escapeHtml(candidate.name)}</strong><span class="muted"><span class="${badgeClass}">${escapeHtml(formatProjectStatus(candidate.status))}</span>${escapeHtml(deadline)}</span>`;
      button.addEventListener("click", () => {
        void withBusy(async () => {
          const detail = await getProject(candidate.id);
          if (screen.name !== "website") {
            return;
          }
          screen = {
            ...screen,
            projectDetail: detail,
            chooserPick: candidate.id,
            active: { kind: "selected", project: candidate },
          };
        });
      });
      li.append(button);
      list.append(li);
    }
    box.append(list);
    if (chooserPick !== null && projectDetail !== null) {
      box.append(projectFacts(projectDetail, members));
    }
    return box;
  }

  if (projectDetail !== null) {
    box.append(projectFacts(projectDetail, members));
    return box;
  }

  box.append(
    mutedNote(
      active.kind === "linked" ? `A carregar projeto ${active.projectId}…` : "A carregar projeto…",
    ),
  );
  return box;
};

const projectFacts = (
  project: ProjectDetail,
  members: ReadonlyMap<string, string>,
): HTMLElement => {
  const rows: FactEntry[] = [
    {
      label: "Estado",
      value: statusBadge(
        formatProjectStatus(project.status),
        isPositiveProjectStatus(project.status),
      ),
    },
    { label: "Responsável", value: responsibleLabel(project.ownerMembershipId, members) },
    { label: "Início", value: formatDate(project.createdAt) },
  ];
  if (project.plannedCompletion !== null) {
    rows.push({ label: "Prazo", value: formatDate(project.plannedCompletion) });
  }
  if (project.description !== null) {
    rows.push({ label: "Descrição", value: project.description });
  }
  rows.push({ label: "Última atualização", value: formatDateTime(project.updatedAt) });
  return factGrid(rows);
};

const statusBadge = (label: string, positive: boolean): HTMLElement => {
  const span = document.createElement("span");
  span.className = positive ? "badge" : "badge inactive";
  span.textContent = label;
  return span;
};

const factGrid = (entries: readonly FactEntry[]): HTMLElement => {
  const grid = document.createElement("div");
  grid.className = "fact-grid";
  for (const entry of entries) {
    const fact = document.createElement("div");
    fact.className = "fact";
    const label = document.createElement("p");
    label.className = "fact-label";
    label.textContent = entry.label;
    const value = document.createElement("div");
    value.className = "fact-value";
    if (typeof entry.value === "string") {
      value.textContent = entry.value;
    } else {
      value.append(entry.value);
    }
    fact.append(label, value);
    grid.append(fact);
  }
  return grid;
};

const renderPalette = (): HTMLElement => {
  const backdrop = document.createElement("div");
  backdrop.className = "palette-backdrop";
  backdrop.addEventListener("click", (event: MouseEvent) => {
    if (event.target === backdrop) {
      paletteOpen = false;
      render();
    }
  });
  const dialog = document.createElement("div");
  dialog.className = "palette";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-label", "Pesquisar");
  const input = document.createElement("input");
  input.id = "palette-q";
  input.type = "search";
  input.placeholder = "Pesquisar clientes, websites e projetos…";
  input.value = paletteQuery;
  input.addEventListener("input", () => {
    paletteQuery = input.value;
    paletteCaret = input.selectionStart ?? paletteQuery.length;
    paletteIndex = 0;
    render();
  });
  dialog.append(input);
  const hits = currentPaletteHits();
  const grouped = groupHits(hits);
  const groups = document.createElement("div");
  groups.className = "palette-groups";
  if (hits.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted palette-empty";
    empty.textContent =
      catalog === null
        ? "A carregar catálogo…"
        : "Nenhum resultado no que já está carregado nesta sessão.";
    groups.append(empty);
  } else {
    let offset = 0;
    offset += appendPaletteGroup(groups, "Clientes", grouped.clients, offset, hits);
    offset += appendPaletteGroup(groups, "Websites", grouped.websites, offset, hits);
    appendPaletteGroup(groups, "Projetos", grouped.projects, offset, hits);
  }
  dialog.append(groups);
  backdrop.append(dialog);
  queueMicrotask(() => {
    input.focus();
    input.setSelectionRange(paletteCaret, paletteCaret);
  });
  return backdrop;
};

const appendPaletteGroup = (
  parent: HTMLElement,
  title: string,
  items: readonly CatalogHit[],
  offset: number,
  all: readonly CatalogHit[],
): number => {
  if (items.length === 0) {
    return 0;
  }
  const group = document.createElement("div");
  group.className = "palette-group";
  const heading = document.createElement("h3");
  heading.textContent = title;
  group.append(heading);
  for (const [index, item] of items.entries()) {
    const globalIndex = offset + index;
    const button = document.createElement("button");
    button.type = "button";
    button.className = globalIndex === paletteIndex ? "palette-hit active" : "palette-hit";
    button.innerHTML = `<strong>${escapeHtml(item.title)}</strong><span class="muted">${escapeHtml(item.subtitle)}</span>`;
    button.addEventListener("click", () => {
      void selectPaletteHit(item);
    });
    button.addEventListener("mouseenter", () => {
      paletteIndex = all.indexOf(item);
      button.className = "palette-hit active";
    });
    group.append(button);
  }
  parent.append(group);
  return items.length;
};

const selectPaletteHit = async (hit: CatalogHit): Promise<void> => {
  closeOverlays();
  await withBusy(
    async () => {
      if (hit.kind === "client") {
        await openClient(hit.id, hit.title);
        return;
      }
      if (hit.kind === "website") {
        await openWebsiteByIds(hit.clientId, hit.id);
        return;
      }
      await openProjectByIds(hit.clientId, hit.id);
    },
    hit.kind === "client" ? { freezeShell: false, skipInitialRender: true } : undefined,
  );
};

const escapeHtml = (value: string): string =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

export const seedOrganizationId = (organizationId: string): void => {
  setOrganizationId(organizationId);
};
